import { useState, useRef, useEffect, useLayoutEffect, type FormEvent, type ReactNode } from 'react';
import OrgLink from '../components/OrgLink';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { ScreenLink as Link } from '../components/permissions/ScreenLink';
import TaskAuditLines from '../components/tasks/TaskAuditLines';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import { withWorkEngagement } from '../components/timer/workLogEngagement';
import { canUseTimer, timerClockStart } from '../components/timer/timerModel';
import { usePermissions } from '../hooks/usePermissions';
import { useAuthStore } from '../store/authStore';
import { useTaskStatuses } from '../hooks/useTaskStatuses';
import { getPriorityLabel, getStatusLabel, getPriorityLabels } from '../types';
import type {
  TicketStatus,
  TicketPriority,
  Ticket,
  TicketAgent,
  TicketAttachment,
  TimelineItem,
  ActiveTimer,
} from '../types';
import {
  Plus, X, Pencil,
  FileText, Archive, Users,
  Download, Trash2, Image, MoreHorizontal,
  Move, CheckCircle2, Circle, Play, Mic, Square, Clock, MailOpen,
  AlertTriangle, Ban, RotateCcw, Link2, PauseCircle, PlayCircle, Flag, Check, ChevronDown, Send, Maximize2, Minimize2,
} from 'lucide-react';
import ForwardTicketDialog from '../components/ticketDetail/ForwardTicketDialog';
import AssignCustomerDialog from '../components/ticketDetail/AssignCustomerDialog';
import { showAssignCustomer } from '../components/ticketDetail/assignCustomerModel';
import TicketSourceIcon, { hasSourceIcon } from '../components/tickets/TicketSourceIcon';
import ConsumptionPreview, { type ConsumptionChoice } from '../components/ConsumptionPreview';
import TicketChatView, { AttachmentTiles } from '../components/TicketChatView';
import TicketCoverageBanner from '../components/customerCoverage/TicketCoverageBanner';
import { readSideWidth, sideWidthFromKey, sideWidthFromPointer, writeSideWidth } from '../components/ticketDetail/ticketPaneModel';

/** D-317 §1 — the user's window width is kept in localStorage (none in a private window: the default). */
const paneStore = (): Storage | null => {
  try { return window.localStorage; } catch { return null; }
};
import WorkingNowBanner from '../components/ticketDetail/WorkingNowBanner';
import HoursProgressBar from '../components/HoursProgressBar';
import DeadlineBanner from '../components/DeadlineBanner';
import DeadlineBadge from '../components/DeadlineBadge';
import StatusBadge from '../components/StatusBadge';
import StatusPillSelect, { type StatusPillOption } from '../components/common/StatusPillSelect';
import VoiceAiBadge from '../components/VoiceAiBadge';
import SlaBadge from '../components/SlaBadge';
import { useSlaPause } from '../components/SlaPauseToggle';
import BugReportBadge from '../components/BugReportBadge';
import BugReportContextPanel from '../components/BugReportContextPanel';
import BugTreatmentPanel from '../components/bugReport/BugTreatmentPanel';
import { AuthenticatedImage, AuthenticatedDownloadLink } from '../components/AuthenticatedImage';
import AttachmentLightbox from '../components/AttachmentLightbox';
import { attachmentKind, openingAttachments } from '../components/media/mediaModel';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import TaskRelationsPanel from '../components/TaskRelationsPanel';
import TaskFormModal from '../components/projects/TaskFormModal';
import TicketTaskLinks from '../components/ticketDetail/TicketTaskLinks';
import TimerSummaryModal, { apiErrorMessage, timerTargetFromTimer } from '../components/TimerSummaryModal';
import { NewWorkLogModal } from '../components/NewWorkLogModal';
import WorkLogOverlapNotice from '../components/worklogs/WorkLogOverlapNotice';
import { parallelConfirmTimes, shortenedTimes, type ShortenedTimes } from '../components/worklogs/overlapModel';
import { useCloseTicket } from '../components/home/useCloseTicket';
import { toast } from '../store/toastStore';
import Badge from '../components/ui/Badge';
import Tabs, { type TabItem } from '../components/ui/Tabs';
import Dialog from '../components/ui/Dialog';
import Popover from '../components/ui/Popover';
import { useMediaQuery } from '../components/ui/useOverlay';
import StateBlock from '../components/ui/StateBlock';
import Field, { FormErrorSummary, focusField } from '../components/ui/Field';
import type { MenuItemDef } from '../components/ui/Menu';
import MenuButton, { useTouchMenus } from '../components/table/MenuButton';
import { priorityTone, statusTone } from '../components/ui/tone';
import { undoableTicketStatus, useShownStatus } from '../utils/undoableTicketStatus';
import { useStartTimer } from '../components/home/useHomeData';
import { ServerError, SubmitButton } from '../components/projects/formBits';
import {
  parseDetailTab, statusSelectLocked, statusSelectOptions, ticketLoadState, ticketPrimary,
  type DetailTab, type TicketPrimary,
} from '../components/ticketDetail/ticketDetailModel';
import { audienceFor } from '../components/ticketDetail/composerModel';
import { OverQuotaBadge, WorkLogSummary } from '../components/worklogs/workLogsTable';
import { PageChrome, type PageChromeAction } from '../hooks/usePageChrome';
import TaskHasWorkDialog, { TicketHasWorkDialog, taskHasWorkMinutes, ticketHasWorkMinutes } from '../components/tasks/TaskHasWorkDialog';
import { pickableMilestones } from '../components/projects/taskFormModel';
import { useBackParent } from '../hooks/useBackNavigation';
import { formatDate, formatDateTime, formatNumber } from '../utils/format';
import { engagementLabel } from '../utils/engagementLabel';
import { canCloseDiscussionUi } from '../utils/discussionParticipants';
import { withoutInternalOnlyOnCustomerWork } from '../utils/customerWorkPeople';
import { toastDebtWarning } from '../utils/debtWarning';
import { toastWorkLogNotices } from '../utils/workLogNotices';
import { undoableDeleteTicket } from '../utils/undoableDeletes';
import EntityPicker from '../components/ui/EntityPicker';
import DateInput from '../components/ui/DateInput';
import InlineTimeRange from '../components/worklogs/InlineTimeRange';
import DateTimeInput from '../components/ui/DateTimeInput';
import TransientAlert from '../components/ui/TransientAlert';
import MoveLogsButton from '../components/worklogs/move/MoveLogsButton';
import { logsToMove } from '../components/worklogs/move/moveLogsModel';

// ============================================================
// Utilities
// ============================================================
function fmtBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function isImageMime(mime: string): boolean {
  return ['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(mime);
}

function AttachmentIcon({ mime, name = '' }: { mime: string; name?: string }) {
  // FIX-B43 (FQ-060): a video / voice file shows a play / mic glyph (it opens in the player), not a document icon.
  const kind = attachmentKind(mime, name);
  if (kind === 'video') return <Play size={18} className="text-primary-700" aria-hidden="true" />;
  if (kind === 'audio') return <Mic size={18} className="text-primary-700" aria-hidden="true" />;
  if (isImageMime(mime)) return <Image size={18} className="text-surface-500" aria-hidden="true" />;
  if (mime.includes('zip')) return <Archive size={18} className="text-surface-500" aria-hidden="true" />;
  return <FileText size={18} className="text-surface-500" aria-hidden="true" />;
}


const ACCEPT = 'image/jpeg,image/png,image/gif,image/webp,application/pdf,' +
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document,' +
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/zip';


type AttachmentWithSource = TicketAttachment & { fromChat?: boolean };

function collectAllAttachments(
  ticketAttachments: TicketAttachment[],
  timeline: TimelineItem[],
): AttachmentWithSource[] {
  const ticketIds = new Set(ticketAttachments.map((a) => a.id));
  const fromComments: AttachmentWithSource[] = [];
  for (const item of timeline) {
    if (item.attachments?.length) {
      for (const att of item.attachments) {
        if (!ticketIds.has(att.id)) {
          fromComments.push({ ...att, fromChat: true });
        }
      }
    }
  }
  const merged: AttachmentWithSource[] = [
    ...ticketAttachments.map((a) => ({ ...a, fromChat: false })),
    ...fromComments,
  ];
  return merged.sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  );
}

// ============================================================
// AttachmentItem
// ============================================================
function AttachmentItem({
  att,
  canDelete,
  onDelete,
  onPreview,
  fromChat,
}: {
  att: TicketAttachment;
  canDelete: boolean;
  onDelete: (id: string) => void;
  onPreview: (att: TicketAttachment) => void;
  fromChat?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <li className="flex items-center gap-3 py-2.5">
      <button
        type="button"
        onClick={() => onPreview(att)}
        className="w-10 h-10 shrink-0 flex items-center justify-center overflow-hidden rounded-lg bg-surface-50 border border-surface-200 hover:border-surface-300"
        aria-label={`${t('tickets.clickToEnlarge')}: ${att.original_name}`}
      >
        {isImageMime(att.mime_type) ? (
          <AuthenticatedImage storageUrl={att.url} alt="" className="w-full h-full object-cover" />
        ) : (
          <AttachmentIcon mime={att.mime_type} name={att.original_name} />
        )}
      </button>
      <div className="flex-1 min-w-0">
        <button
          type="button"
          onClick={() => onPreview(att)}
          className="block w-full text-start text-sm text-surface-900 hover:text-primary-700 hover:underline truncate"
          title={att.original_name}
          dir="auto"
        >
          {att.original_name}
        </button>
        <p className="text-xs text-surface-600 tabular-nums">
          {fmtBytes(att.file_size_bytes)} · {formatDate(att.created_at)}
          {fromChat && <> · {t('ticketDetail.fromChat')}</>}
        </p>
      </div>
      <div className="flex items-center gap-0.5 shrink-0">
        <AuthenticatedDownloadLink
          storageUrl={att.url}
          filename={att.original_name}
          className="p-2 rounded-md text-surface-500 hover:text-surface-900 hover:bg-surface-100 transition-colors duration-150"
        >
          <span title={t('common.download')} aria-label={`${t('common.download')}: ${att.original_name}`}><Download size={16} aria-hidden="true" /></span>
        </AuthenticatedDownloadLink>
        {canDelete && (
          <button type="button" onClick={() => onDelete(att.id)}
            className="p-2 rounded-md text-surface-500 hover:text-danger-700 hover:bg-danger-50 transition-colors duration-150"
            title={t('common.delete')} aria-label={`${t('common.delete')}: ${att.original_name}`}>
            <Trash2 size={16} aria-hidden="true" />
          </button>
        )}
      </div>
    </li>
  );
}

// ============================================================
// TicketAttachmentsCard
// ============================================================
function TicketAttachmentsCard({
  attachments,
  ticketId,
  userId,
  userRole,
  onSuccess,
  compact = false,
  tabMode = false,
}: {
  attachments: AttachmentWithSource[];
  ticketId: string;
  userId: string;
  userRole: string;
  onSuccess: () => void;
  compact?: boolean;
  tabMode?: boolean;
}) {
  const { t } = useTranslation();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [previewAtt, setPreviewAtt] = useState<TicketAttachment | null>(null);
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: (attId: string) => api.deleteAttachment(ticketId, attId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ticket-timeline', ticketId] }),
  });

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setProgress(0);
    try {
      await api.uploadTicketAttachment(ticketId, file, setProgress);
      onSuccess();
    } catch (err) {
      toast('error', apiErrorMessage(err) || t('ticketDetail.uploadError'));
    } finally {
      setUploading(false);
      setProgress(0);
      e.target.value = '';
    }
  };

  return (
    <div className={tabMode ? '' : 'card'}>
      {previewAtt && <AttachmentLightbox att={previewAtt} onClose={() => setPreviewAtt(null)} />}
      <div className="flex items-center justify-between gap-3 mb-2">
        {tabMode ? (
          <span className="text-sm text-surface-600 tabular-nums">{attachments.length > 0 ? t('uxTickets.attachmentsCount', { count: attachments.length }) : ''}</span>
        ) : (
          <h3 className={`${compact ? 'text-sm' : 'text-base'} font-semibold text-surface-900`}>{t('ticketDetail.attachments')}</h3>
        )}
        <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
          className="btn btn-secondary btn-sm">
          <Plus size={14} aria-hidden="true" /> {uploading ? `${t('common.uploading')} ${progress}%` : t('ticketDetail.uploadFile')}
        </button>
        <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" onChange={handleFile} />
      </div>
      {deleteMutation.isError && (
        <p className="error-text mb-2" role="alert">{t('ticketDetail.deleteAttachmentError')}</p>
      )}
      {attachments.length === 0 ? (
        <p className="text-sm text-surface-600 py-6 text-center">{t('ticketDetail.noAttachments')}</p>
      ) : (
        <ul className="divide-y divide-surface-100">
          {attachments.map((att) => (
            <AttachmentItem
              key={att.id}
              att={att}
              canDelete={att.uploader_id === userId || userRole === 'admin'}
              onDelete={(id) => deleteMutation.mutate(id)}
              onPreview={setPreviewAtt}
              fromChat={att.fromChat}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

// ============================================================
// WorkLogsCard (collapsible)
// ============================================================
export function WorkLogsCard({ workLogs, canAddLog, ticketId, onAddSuccess, move }: {
  workLogs: any[];
  canAddLog: boolean;
  ticketId: string;
  onAddSuccess: () => void;
  /** D-348: "העברת לוגים להתקשרות אחרת" - the task's customer / engagement / name (absent = no button). */
  move?: { organizationId?: string | null; engagementId?: string | null; title?: string | null };
}) {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === 'admin';
  const isAgent = user?.role === 'agent';
  const [showAdd, setShowAdd] = useState(false);
  // FIX-MOVE-LOGS-SELECT: logs checked for the move (only rows the user may move get a box).
  const [picked, setPicked] = useState<Set<string>>(() => new Set());
  const canMoveLog = (l: any) => isAdmin || l.agent_id === user?.id;
  const togglePicked = (id: string) => setPicked((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const totalMinutes = workLogs.reduce((s, wl) => s + Number(wl.duration_minutes ?? 0), 0);

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-2">
        <span className="text-sm text-surface-600 tabular-nums">
          {t('uxTickets.workLogsSummary', { count: workLogs.length, hours: formatNumber(totalMinutes / 60, 1) })}
        </span>
        <div className="flex flex-wrap items-center gap-2">
          {move && (isAdmin || isAgent) && (
            <MoveLogsButton
              testId="task-move-logs"
              // an employee moves their own logs; the OTE manager every log of the task
              logs={logsToMove(workLogs.filter(canMoveLog).map((l) => ({
                id: l.id, organization_id: move.organizationId ?? l.organization_id, engagement_id: move.engagementId ?? null,
                ticket_id: ticketId, ticket_title: move.title ?? null, duration_minutes: l.duration_minutes,
                start_time: l.start_time ?? null, agent_name: l.agent_name ?? null, summary: l.summary ?? null,
              })), picked)}
              onMoved={() => { setPicked(new Set()); onAddSuccess(); }}
            />
          )}
          {canAddLog && (
            <button type="button" onClick={() => setShowAdd(true)} className="btn btn-secondary btn-sm">
              <Plus size={14} aria-hidden="true" /> {t('ticketDetail.newWorkLog')}
            </button>
          )}
        </div>
      </div>
      {showAdd && (
        <AddWorkLogModal ticketId={ticketId} onClose={() => setShowAdd(false)} onSuccess={() => { onAddSuccess(); setShowAdd(false); }} />
      )}
      {workLogs.length === 0 ? (
        <p className="text-sm text-surface-600 text-center py-6">{t('ticketDetail.noWorkLogs')}</p>
      ) : (
        <ul className="divide-y divide-surface-100">
          {workLogs.map((log) => (
            <li key={log.id} className="flex items-start gap-4 py-3">
              {move && (isAdmin || isAgent) && canMoveLog(log) && (
                <input type="checkbox" className="mt-1 shrink-0" checked={picked.has(log.id)} onChange={() => togglePicked(log.id)}
                  aria-label={t('moveLogs.selectLog', { date: log.start_time ? formatDate(log.start_time) : '' })} data-testid="task-log-select" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-surface-900">{log.agent_name}</span>
                  {log.requires_review && !log.reviewed && <Badge tone="warning">{t('workLogs.pendingReview')}</Badge>}
                  {/* D-266 sec.1: beyond the quota / the bank - waits for the approval / a new bank */}
                  <OverQuotaBadge log={log} t={t} />
                  {log.source === 'voice_ai' && <VoiceAiBadge confidence={log.ai_confidence} />}
                </div>
                {log.summary && (
                  <p className="text-sm text-surface-700 mt-0.5 line-clamp-2">
                    <WorkLogSummary summary={log.summary} ended={!!log.end_time} t={t} className="" />
                  </p>
                )}
                {/* DQ-043: an employee edits only their own log's times (the server refuses others'); the OTE manager everyone's */}
                {(isAdmin || (isAgent && log.agent_id === user?.id)) && log.start_time && (
                  <p className="text-xs text-surface-600 mt-1 tabular-nums">
                    {/* D-292: the start / end time is edited in place */}
                    <InlineTimeRange log={log} canEdit onSaved={onAddSuccess} className="justify-start" />
                  </p>
                )}
              </div>
              <div className="shrink-0 text-end">
                <div className="text-sm font-medium text-surface-900 tabular-nums">{log.duration_minutes} {t('common.minutesShort')}</div>
                <div className="text-xs text-surface-600">{log.billable ? t('common.billable') : t('common.nonBillable')}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ============================================================
// MoveTicketModal
// ============================================================
function MoveTicketModal({ ticketId, currentProjectId, currentMilestoneId, onClose, onSuccess }: {
  ticketId: string;
  currentProjectId?: string;
  currentMilestoneId?: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const [projectId, setProjectId] = useState(currentProjectId || '');
  const [milestoneId, setMilestoneId] = useState(currentMilestoneId || '');
  const [hoursAction, setHoursAction] = useState<'move' | 'keep' | 'split'>('move');

  const { data: projectsData } = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.getProjects({ limit: 100 }),
  });

  const { data: milestonesData } = useQuery({
    // D-280 (D-63): the same milestone choices as the task form.
    queryKey: ['milestones', projectId, 'task-choices'],
    queryFn: () => api.getMilestoneTaskChoices(projectId),
    enabled: !!projectId,
  });

  const projects = projectsData?.data ?? [];
  const milestones = pickableMilestones(milestonesData?.data ?? [], currentMilestoneId);

  const mutation = useMutation({
    mutationFn: () => api.moveTicket(ticketId, {
      project_id: projectId || null,
      milestone_id: milestoneId || null,
      hours_action: hoursAction,
    }),
    onSuccess,
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('ticketDetail.moveTicket')}
      size="md"
      dismissible={!mutation.isPending}
      footer={(
        <>
          <button type="button" onClick={onClose} className="btn btn-secondary" disabled={mutation.isPending}>{t('common.cancel')}</button>
          <SubmitButton form="move-ticket-form" busy={mutation.isPending}>{t('ticketDetail.move')}</SubmitButton>
        </>
      )}
    >
      <form id="move-ticket-form" onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }} className="space-y-4">
        <ServerError error={mutation.error} fallback={t('ticketDetail.moveFailed')} />
        <Field label={t('tickets.project')} id="move-project">
          <EntityPicker
            emptyLabel={t('tickets.noProject')}
            options={projects.map((p) => ({ value: p.id, label: p.name }))}
            value={projectId}
            onChange={(id) => { setProjectId(id); setMilestoneId(''); }}
          />
        </Field>

        {projectId && milestones.length > 0 && (
          <Field label={t('tickets.milestone')} id="move-milestone">
            <EntityPicker
              emptyLabel={t('tickets.noMilestone')}
              options={milestones.map((m) => ({ value: m.id, label: m.name }))}
              value={milestoneId}
              onChange={setMilestoneId}
            />
          </Field>
        )}

        <fieldset>
          <legend className="label">{t('ticketDetail.hours.legend')}</legend>
          <div className="space-y-2">
            {[
              { value: 'move' as const, label: t('ticketDetail.hours.move') },
              { value: 'keep' as const, label: t('ticketDetail.hours.keep') },
              { value: 'split' as const, label: t('ticketDetail.hours.split') },
            ].map((opt) => (
              <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="hours_action" value={opt.value}
                  checked={hoursAction === opt.value} onChange={() => setHoursAction(opt.value)}
                  className="w-4 h-4 accent-primary-700" />
                <span className="text-sm text-surface-700">{opt.label}</span>
              </label>
            ))}
          </div>
        </fieldset>
      </form>
    </Dialog>
  );
}


// ============================================================
// LinkMilestoneModal — "שיוך לאבן דרך": link this task UP to an existing
// milestone of its project (tickets.milestone_id). Canonical direction:
// project → milestone → task; a milestone is never created from a task.
// ============================================================
function LinkMilestoneModal({ ticketId, projectId, currentMilestoneId, onClose, onSuccess }: {
  ticketId: string;
  projectId: string;
  currentMilestoneId?: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const [milestoneId, setMilestoneId] = useState(currentMilestoneId || '');

  const { data: milestonesData, isLoading } = useQuery({
    // D-280 (D-63): the same milestone choices as the task form.
    queryKey: ['milestones', projectId, 'task-choices'],
    queryFn: () => api.getMilestoneTaskChoices(projectId),
  });
  const milestones = pickableMilestones(milestonesData?.data ?? [], currentMilestoneId);

  const mutation = useMutation({
    // No milestone = the task stays directly under its project.
    mutationFn: () => api.moveTicket(ticketId, { project_id: projectId, milestone_id: milestoneId || null }),
    onSuccess,
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('ticketDetail.linkMilestone')}
      size="md"
      dismissible={!mutation.isPending}
      footer={(
        <>
          <button type="button" onClick={onClose} className="btn btn-secondary" disabled={mutation.isPending}>{t('common.cancel')}</button>
          <SubmitButton form="link-milestone-form" busy={mutation.isPending} disabled={milestoneId === (currentMilestoneId || '')}>
            {t('ticketDetail.linkMilestoneSave')}
          </SubmitButton>
        </>
      )}
    >
      <form id="link-milestone-form" onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }} className="space-y-4">
        <ServerError error={mutation.error} fallback={t('ticketDetail.linkMilestoneFailed')} />
        <Field
          label={t('tickets.milestone')}
          id="link-milestone"
          hint={!isLoading && milestones.length === 0 ? t('ticketDetail.linkMilestoneNone') : undefined}
        >
          <EntityPicker
            emptyLabel={t('tickets.noMilestone')}
            options={milestones.map((m) => ({ value: m.id, label: m.name }))}
            value={milestoneId}
            onChange={setMilestoneId}
            loading={isLoading}
          />
        </Field>
      </form>
    </Dialog>
  );
}

// ============================================================
// SubtasksSection
// ============================================================
function SubtasksSection({ ticketId, isParent }: { ticketId: string; isParent: boolean }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [title, setTitle] = useState('');
  const [estimatedHours, setEstimatedHours] = useState('');

  const { data: subtasksData } = useQuery({
    queryKey: ['subtasks', ticketId],
    queryFn: () => api.getSubtasks(ticketId),
    enabled: isParent,
  });

  const createMutation = useMutation({
    mutationFn: () => api.createSubtask(ticketId, {
      title,
      estimated_hours: estimatedHours ? parseFloat(estimatedHours) : undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subtasks', ticketId] });
      setTitle('');
      setEstimatedHours('');
      setShowAdd(false);
    },
    onError: (err) => toast('error', apiErrorMessage(err) || t('common.saveError')),
  });

  const subtasks = subtasksData?.data ?? [];
  if (!isParent) return null;

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-2">
        <span className="text-sm text-surface-600 tabular-nums">{subtasks.length} {t('tickets.subtasks')}</span>
        <button type="button" onClick={() => setShowAdd(!showAdd)} aria-expanded={showAdd} className="btn btn-secondary btn-sm">
          <Plus size={14} aria-hidden="true" /> {t('tickets.addSubtask')}
        </button>
      </div>

      {showAdd && (
        <form onSubmit={(e) => { e.preventDefault(); createMutation.mutate(); }}
          className="flex flex-wrap items-center gap-2 mb-3 p-3 bg-surface-50 rounded-lg">
          <input className="input flex-1 min-w-48" placeholder={t('tickets.subtaskTitle')} aria-label={t('tickets.subtaskTitle')} value={title}
            onChange={(e) => setTitle(e.target.value)} required minLength={3} />
          <input type="number" step="0.5" className="input w-24" placeholder={t('common.hours')} aria-label={t('tickets.estimatedHours')}
            value={estimatedHours} onChange={(e) => setEstimatedHours(e.target.value)} />
          <button type="submit" disabled={createMutation.isPending} className="btn btn-primary btn-sm">
            {createMutation.isPending ? t('common.creating') : t('common.create')}
          </button>
          <button type="button" onClick={() => setShowAdd(false)} className="btn btn-ghost btn-sm">
            {t('common.cancel')}
          </button>
        </form>
      )}

      {subtasks.length === 0 ? (
        <p className="text-sm text-surface-600 text-center py-6">{t('tickets.noSubtasks')}</p>
      ) : (
        <ul className="divide-y divide-surface-100">
          {subtasks.map((st: Ticket) => (
            <li key={st.id}>
              <Link to={`/tickets/${st.id}`}
                className="flex items-center justify-between gap-3 py-2.5 -mx-2 px-2 rounded-lg hover:bg-surface-50 transition-colors duration-150">
                <span className="flex items-center gap-2 min-w-0">
                  {st.status === 'completed' ? (
                    <CheckCircle2 size={16} className="text-success-600 shrink-0" aria-hidden="true" />
                  ) : (
                    <Circle size={16} className="text-surface-400 shrink-0" aria-hidden="true" />
                  )}
                  <span className={`text-sm truncate ${st.status === 'completed' ? 'text-surface-500 line-through' : 'text-surface-900'}`} dir="auto">
                    {st.title}
                  </span>
                </span>
                <span className="flex items-center gap-3 text-xs text-surface-600 shrink-0">
                  {st.estimated_hours != null && (
                    <span className="tabular-nums">{st.actual_hours}/{st.estimated_hours} {t('common.hoursShort')}</span>
                  )}
                  <StatusBadge status={st.status} />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ============================================================
// Main Page
// ============================================================
/**
 * Ticket detail (T-44, PRD §6.3, controls inventory §4.3 / §4.4).
 * - Header: eyebrow "TKT-#### · לקוח", h1, badges; ONE primary chosen by
 *   state (`ticketPrimary`): התחלת עבודה · עצירה ורישום · סגירת הקריאה
 *   (`useCloseTicket`, delayed commit + "ביטול", B.23) · פתיחה מחדש ·
 *   ביטול הביטול. "עריכה" is the first item of ⋯ and appears on hover of the
 *   header (pointer devices); everything else lives in ⋯.
 * - A project task adds the visible secondary "רישום שעות" (D7).
 * - Tabs in `?tab=` (back / forward restore them).
 * - The side-panel status select never offers "הושלם" and is locked while
 *   the ticket is completed or cancelled.
 * - 404 / 403 → "הקריאה לא נמצאה או שאין לך הרשאה לצפות בה"; other failures
 *   → an error with "ניסיון חוזר".
 */
export default function TicketDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const touch = useTouchMenus();
  // Below lg the side panel drops under the conversation, so its facts (status,
  // priority, owner) move into a folded "פרטים" row under the title.
  const wide = useMediaQuery('(min-width: 1024px)');
  const [factsOpen, setFactsOpen] = useState(false);
  // C-33 (clarity, N-02): where the conversation pane starts in the document (desktop height = the rest of the screen).
  const chatPaneRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = chatPaneRef.current;
    if (!el) return undefined;
    const measure = () => { el.style.setProperty('--chat-top', `${Math.round(el.getBoundingClientRect().top + window.scrollY)}px`); };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  });
  const [editing, setEditing] = useState(false);

  const isAdmin = user?.role === 'admin';
  const isAgent = user?.role === 'agent';
  const isStaff = !!(isAdmin || isAgent);
  // FIX-OPENREC — the timer (start / stop on this page) follows the server's rule: any OTE employee.
  const { isOteEmployee: viewerIsOteEmployee } = usePermissions();
  const canTimer = !!user && canUseTimer(user.role, viewerIsOteEmployee);

  // Delete ticket mutation
  // P-27 (D-166 s.11): hide at once, DELETE after 6 s, "בטל" cancels it and brings
  // the page back. A refusal (logged hours) returns here with the same dialogs.
  const requestDeleteTicket = () => {
    const here = `${location.pathname}${location.search}`;
    undoableDeleteTicket({
      qc: queryClient, id: id!, taskType: ticket?.task_type, isConversation: ticket?.is_conversation === true,
      onRestore: () => navigate(here, { replace: true }),
      onError: (err) => {
        const minutes = taskHasWorkMinutes(err);
        const ticketMinutes = ticketHasWorkMinutes(err);
        if (minutes !== null || ticketMinutes !== null) {
          navigate(here, { replace: true, state: { hasWorkMinutes: minutes, ticketHasWorkMinutes: ticketMinutes } });
        } else {
          toast('error', apiErrorMessage(err) || t('common.deleteError'));
          navigate(here, { replace: true });
        }
      },
    });
    const from = searchParams.get('from');
    navigate(from || '/tickets');
  };
  // P-02a/C1 — the refused delete of a task with hours (minutes logged on it).
  const [hasWorkMinutes, setHasWorkMinutes] = useState<number | null>(null);
  // P-02c/F-04 — the same for a service ticket (it can be closed instead).
  const [ticketHasWorkMinutesState, setTicketHasWorkMinutes] = useState<number | null>(null);
  useEffect(() => {
    const st = location.state as { hasWorkMinutes?: number | null; ticketHasWorkMinutes?: number | null } | null;
    if (typeof st?.hasWorkMinutes === 'number') setHasWorkMinutes(st.hasWorkMinutes);
    else if (typeof st?.ticketHasWorkMinutes === 'number') setTicketHasWorkMinutes(st.ticketHasWorkMinutes);
  }, [location.state]);

  // P-39 (D-199 36-1): opening the ticket = read (also when the chat tab is not the open one);
  // "סמן כלא נקרא" is personal and is cleared by the next open.
  useEffect(() => {
    if (id) api.markTicketChatRead(id).then(() => {
      void queryClient.invalidateQueries({ queryKey: ['tickets'] });
      void queryClient.invalidateQueries({ queryKey: ['workspace'] });
    }).catch(() => { /* non-blocking */ });
  }, [id, queryClient]);
  const markUnreadMutation = useMutation({
    mutationFn: () => api.markTicketUnread(id!),
    onSuccess: () => {
      for (const key of ['tickets', 'conversations', 'conversations-grouped', 'workspace', 'home']) {
        void queryClient.invalidateQueries({ queryKey: [key] });
      }
      toast('success', t('ticketDetail.markedUnread'));
    },
    onError: () => toast('error', t('ticketDetail.markUnreadFailed')),
  });

  // §18 — cancel is a SEPARATE action from status. It sets cancel_reason + cancelled_at.
  const [showCancelTicket, setShowCancelTicket] = useState(false);
  const cancelTicketMutation = useMutation({
    mutationFn: (reason: string) => api.cancelTicket(id!, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
      queryClient.invalidateQueries({ queryKey: ['ticket-timeline', id] });
      setShowCancelTicket(false);
      toast('success', t('ticketDetail.cancelled'));
    },
  });

  const uncancelTicketMutation = useMutation({
    mutationFn: () => api.uncancelTicket(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
      queryClient.invalidateQueries({ queryKey: ['ticket-timeline', id] });
      toast('success', t('ticketDetail.uncancelled'));
    },
    onError: (err) => toast('error', apiErrorMessage(err) || t('common.actionFailed')),
  });

  // B.23 — a completed task is reopened only through this explicit action.
  const [showReopen, setShowReopen] = useState(false);
  const reopenTicketMutation = useMutation({
    mutationFn: (reason: string) => api.reopenTicket(id!, { reason: reason || undefined }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
      queryClient.invalidateQueries({ queryKey: ['ticket-timeline', id] });
      setShowReopen(false);
      toast('success', t('ticketDetail.reopened'));
    },
  });

  const { data: ticketData, isLoading: ticketLoading, error: ticketError, refetch: refetchTicket, isFetching: ticketFetching } = useQuery({
    queryKey: ['ticket', id],
    queryFn: () => api.getTicket(id!),
    enabled: !!id,
  });

  const { data: timelineData, isLoading: timelineLoading } = useQuery({
    queryKey: ['ticket-timeline', id],
    queryFn: () => api.getTicketTimeline(id!),
    enabled: !!id,
  });

  const { data: workLogsData } = useQuery({
    queryKey: ['work-logs', { ticket_id: id }],
    queryFn: () => api.getWorkLogs({ ticket_id: id, limit: 100 }),
    enabled: !!id,
  });

  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.getUsers({ limit: 100 }),
    enabled: !!user && (user.role === 'admin' || user.role === 'agent'),
  });

  // The running timers (the same cache entry GlobalTimer keeps fresh): is mine on this ticket?
  const { data: timerData } = useQuery({
    queryKey: ['timer-status'],
    queryFn: () => api.getTimerStatus(),
    enabled: isStaff || canTimer,
    staleTime: 0,
  });
  // P-37 (D-194) — "אני על זה" (kind 'reply') lives next to the composer; this is a regular (older) timer.
  const myTimer: ActiveTimer | null = (timerData?.data?.timers ?? []).find((tm: ActiveTimer) => tm.ticket_id === id && tm.status === 'running' && tm.kind !== 'reply') ?? null;
  const [stopping, setStopping] = useState<ActiveTimer | null>(null);

  const addAgentMutation = useMutation({
    mutationFn: (agentId: string) => api.addTicketAgent(id!, agentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
      queryClient.invalidateQueries({ queryKey: ['ticket-timeline', id] });
    },
  });

  const removeAgentMutation = useMutation({
    mutationFn: (agentId: string) => api.removeTicketAgent(id!, agentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
      queryClient.invalidateQueries({ queryKey: ['ticket-timeline', id] });
    },
  });

  // The shared start: asks "stop and switch" / "run both" when another task's
  // timer runs. Errors stay inline on this page (the alert under the header).
  const startTimerMutation = useStartTimer({ silentErrors: true });

  // P-29 (QX-107): the change is saved after the 6 s "ביטול" window (the customer notice goes then);
  // the stepper shows the new status at once.
  const statusStepperMutation = {
    mutate: (newStatus: string) => { undoableTicketStatus({ qc: queryClient, id: id!, status: newStatus, taskType: ticket?.task_type ?? null }); },
    isPending: false,
    isError: false,
    error: null as unknown,
    reset: () => {}, // D-285: it never errors here; kept so the shared ✕ resets every mutation shown below
  };

  // Close = delayed commit with "ביטול" (B.23): the ticket shows as closing during the window.
  const [closing, setClosing] = useState(false);
  const close = useCloseTicket({
    onHide: () => setClosing(true),
    // Undone, or the request failed: the ticket is as it was.
    onShow: () => {
      setClosing(false);
      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
    },
    // The close was sent and accepted: reload the real ticket, then drop the "closing" state.
    onCommitted: () => {
      void Promise.all([
        queryClient.invalidateQueries({ queryKey: ['ticket', id] }),
        queryClient.invalidateQueries({ queryKey: ['ticket-timeline', id] }),
      ]).finally(() => setClosing(false));
    },
  });

  const [showMoveModal, setShowMoveModal] = useState(false);
  // D-144 / D-145 — "שליחה לעובד אחר" (+ internal note).
  const [showForward, setShowForward] = useState(false);
  // D-317 §1 — the ticket window: its side panel's width (dragged, kept per user) and "מסך מלא".
  const [sideW, setSideW] = useState(() => readSideWidth(paneStore(), user?.id));
  const sideWRef = useRef(sideW);
  sideWRef.current = sideW;
  const [paneFull, setPaneFull] = useState(false);
  const paneGridRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!paneFull) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setPaneFull(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [paneFull]);
  // The conversation pane measures its height from where it starts: re-measure after the window changes size.
  useEffect(() => { window.dispatchEvent(new Event('resize')); }, [paneFull]);
  // QA-D316-317 #1 — full screen: the floating "חדש" steps aside (styles: body[data-ticket-full] .qc-fab-root).
  useEffect(() => {
    if (!paneFull) return undefined;
    document.body.dataset.ticketFull = '';
    return () => { delete document.body.dataset.ticketFull; };
  }, [paneFull]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [showDeleteTicket, setShowDeleteTicket] = useState(false);
  const [showLinkMilestone, setShowLinkMilestone] = useState(false);
  // P-37 (D-194 Q-179) — "יצירת משימה מהקריאה": the regular task form, filled from the ticket.
  const [createTaskFromTicket, setCreateTaskFromTicket] = useState(false);
  const [logTimeFor, setLogTimeFor] = useState<Ticket | null>(null);
  const [relationsSignal, setRelationsSignal] = useState(0);

  const ticket = ticketData?.data;
  const shownStatus = useShownStatus(ticket?.id, ticket?.status ?? '');
  // TopBar "חזרה" on a direct link: a project task goes up to the tasks list.
  useBackParent(ticket?.task_type === 'task' ? '/tasks' : null);
  const sla = useSlaPause(id ?? '', !!id && !!ticket && isStaff && !!(ticket.sla_response_due_at || ticket.sla_resolution_due_at));
  const canEdit = user && ['admin', 'agent', 'customer_manager'].includes(user.role);

  useEffect(() => {
    if (searchParams.get('edit') === '1' && canEdit && ticket) {
      setEditing(true);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('edit');
        return next;
      }, { replace: true });
    }
  }, [searchParams, canEdit, ticket, setSearchParams]);

  // `?action=cancel` (P-02a/C1 — "ביטול המשימה" after a refused delete in a list).
  useEffect(() => {
    if (searchParams.get('action') === 'cancel' && isStaff && ticket) {
      if (!ticket.cancelled_at) setShowCancelTicket(true);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('action');
        return next;
      }, { replace: true });
    }
  }, [searchParams, isStaff, ticket, setSearchParams]);

  // `?chat=1` (deep links from notifications): the conversation tab.
  useEffect(() => {
    if (searchParams.get('chat') === '1') {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('chat');
        next.delete('tab');
        return next;
      }, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  if (ticketLoading) {
    return (
      <div className="max-w-7xl mx-auto animate-pulse" aria-busy="true">
        <div className="h-4 w-40 bg-surface-100 rounded-xs mb-3" />
        <div className="h-8 w-80 max-w-full bg-surface-100 rounded-lg mb-3" />
        <div className="h-5 w-56 bg-surface-100 rounded-xs mb-6" />
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_20rem] gap-6">
          <div className="h-96 bg-surface-0 border border-surface-200 rounded-xl" />
          <div className="h-80 bg-surface-0 border border-surface-200 rounded-xl" />
        </div>
      </div>
    );
  }
  if (!ticket) {
    const back = <Link to="/tickets" className="btn btn-secondary">{t('tickets.backToTickets')}</Link>;
    const state = ticketError ? ticketLoadState(ticketError) : 'notFound';
    return (
      <div className="max-w-7xl mx-auto rounded-xl border border-surface-200 bg-surface-0">
        {state === 'notFound' ? (
          <>
            {/* The page's h1 for screen readers; the visible line is the state's own title (same text, hidden from AT). */}
            <h1 className="sr-only">{t('ticketDetail.notFoundTitle')}</h1>
            <StateBlock
              kind="unavailable"
              size="page"
              title={<span aria-hidden="true">{t('ticketDetail.notFoundTitle')}</span>}
              description={t('ticketDetail.notFoundText')}
              action={back}
            />
          </>
        ) : (
          <StateBlock
            kind="error"
            size="page"
            // Inside the alert, so it is announced once and still navigable as the page heading.
            title={<span role="heading" aria-level={1}>{t('ticketDetail.loadError')}</span>}
            error={ticketError}
            onRetry={() => refetchTicket()}
            retrying={ticketFetching}
            action={back}
          />
        )}
      </div>
    );
  }

  // P-06/B (D-58, T-14) — a task has its own page: every link to a task
  // (lists, notifications, old bookmarks) lands there, and the menu marks "משימות".
  if (ticket.task_type === 'task') {
    return <Navigate to={`/tasks/${ticket.id}${window.location.search}`} replace />;
  }
  // FIX-BUG-CARD-PUSH (D-323 / D-341 (5)): a bug report is not a ticket card - every way in opens the simple report screen.
  if (ticket.bug_report_context_id && isStaff) {
    return <Navigate to={`/bug-reports/${ticket.id}`} replace />;
  }

  const timeline = timelineData?.data?.timeline ?? [];
  const ticketAttachments = timelineData?.data?.ticket_attachments ?? [];
  const workLogs = workLogsData?.data ?? [];
  const allUsers = usersData?.data ?? [];
  // D-196: an internal-only employee is not offered on a customer's ticket (the company's own: yes).
  const customerWork = !!ticket.organization_id && ticket.organization_id !== user?.organization_id;
  const agents = withoutInternalOnlyOnCustomerWork(allUsers.filter((u) => u.role === 'agent' || u.role === 'admin'), customerWork);
  const assignedAgents: TicketAgent[] = ticket.agents ?? [];

  const isCustomerRole = user && ['customer_manager', 'customer_viewer'].includes(user.role);
  // D-317 §6 (Q3) — "העברה": the employee the ticket is with, or a manager of it (the server checks the same).
  const mayForward = !!isStaff && ticket.task_type === 'ticket' && !ticket.cancelled_at
    && (ticket.assigned_agent_id === user?.id || assignedAgents.some((a) => a.id === user?.id) || ticket.can_manage_task === true);
  // D-265 round 4 (#1 completion): a task of an engagement in the archive is read only - no status,
  // no edit, no actions, no hours, no reply (the server refuses them, 409).
  const archivedLock = (ticket as { engagement_archived?: boolean }).engagement_archived === true;
  const canChangeStatus = isStaff && !archivedLock;
  const canAddLog = isStaff && !archivedLock;
  const canSeeInternal = isStaff;

  const isParentTicket = !ticket.parent_ticket_id;
  const allAttachments = collectAllAttachments(ticketAttachments, timeline);
  // FIX-B43 (FQ-060): the files attached when the request was opened go into the conversation's first bubble.
  const openingFiles = openingAttachments(ticketAttachments, ticket?.created_at, timeline.flatMap((i) => (i.attachments ?? []).map((a) => a.id)));
  const showSubtasksTab = isParentTicket && user?.role !== 'customer_viewer';
  // Tasks are redirected above (P-06/B); the flag stays for the shared wording.
  const isProjectTask = (ticket.task_type as string) === 'task';
  const detailTabs: TabItem<DetailTab>[] = [
    { key: 'chat', label: t('ticketDetail.tabChat') },
    ...(showSubtasksTab ? [{ key: 'subtasks' as DetailTab, label: t('ticketDetail.tabSubtasks') }] : []),
    ...(canSeeInternal ? [{ key: 'worklogs' as DetailTab, label: t('ticketDetail.tabWorkLogs'), count: workLogs.length || undefined }] : []),
    { key: 'attachments', label: t('ticketDetail.tabAttachments'), count: allAttachments.length || undefined },
  ];
  const activeTab = parseDetailTab(searchParams.get('tab'), detailTabs.map((d) => d.key));
  // A push (not a replace), so back / forward walk through the tabs.
  const setActiveTab = (tab: DetailTab) => setSearchParams((prev) => {
    const next = new URLSearchParams(prev);
    if (tab === 'chat') next.delete('tab'); else next.set('tab', tab);
    return next;
  });

  const totalMinutes = workLogs.reduce((s, wl) => s + Number(wl.duration_minutes ?? 0), 0);
  const billableMinutes = workLogs.filter((wl) => wl.billable).reduce((s, wl) => s + Number(wl.duration_minutes ?? 0), 0);
  const pTone = priorityTone(ticket.priority);
  const dueDate = ticket.effective_due_date || ticket.due_date;
  const hasSla = !!(ticket.sla_response_due_at || ticket.sla_resolution_due_at);
  const cancelled = !!ticket.cancelled_at;

  // QA-P36 (D-193 Q-168): a discussion is closed by its opener and the OTE manager only.
  const mayClose = canCloseDiscussionUi(ticket, user);
  const primaryRaw: TicketPrimary = closing ? null : ticketPrimary({
    isStaff: isStaff || canTimer,
    status: ticket.status,
    cancelled,
    timerRunningHere: !!myTimer,
    taskType: ticket.task_type,
    mayClose,
  });
  // FIX-OPENREC — an OTE employee with another login role gets only the timer (start / stop) here.
  const primary: TicketPrimary = isStaff || primaryRaw === 'start' || primaryRaw === 'stop' ? primaryRaw : null;
  const closeItem = { id: ticket.id, title: ticket.title, task_type: ticket.task_type, parent_ticket_id: ticket.parent_ticket_id };
  const canEditDetails = !!canEdit && !isProjectTask && !editing && !archivedLock;
  const openEdit = () => { setEditing(true); requestAnimationFrame(() => focusField('et-title')); };

  const moreActions: MenuItemDef[] = [
    ...(canEditDetails ? [{ key: 'edit', label: t('ticketDetail.editDetails'), icon: <Pencil size={16} strokeWidth={1.75} />, onSelect: openEdit }] : []),
    ...(isStaff && primary === 'close' && ticket.task_type !== 'ticket' ? [{
      key: 'start', label: t('ticketDetail.startWork'), icon: <Play size={16} strokeWidth={1.75} />,
      disabled: startTimerMutation.isPending, onSelect: () => startTimerMutation.mutate({ id: ticket.id, title: ticket.title }),
    }] : []),
    ...(isStaff && mayClose && (primary === 'start' || primary === 'stop') ? [{
      key: 'close', label: isProjectTask ? t('ticketDetail.closeTask') : t('ticketDetail.closeTicket'),
      icon: <CheckCircle2 size={16} strokeWidth={1.75} />, disabled: !!close.checking, onSelect: () => void close.request(closeItem),
    }] : []),
    ...(isStaff && hasSla && sla.available ? [{
      key: 'sla', label: sla.paused ? t('ticketDetail.slaResume') : t('ticketDetail.slaPause'),
      icon: sla.paused ? <PlayCircle size={16} strokeWidth={1.75} /> : <PauseCircle size={16} strokeWidth={1.75} />,
      disabled: sla.pending, onSelect: sla.toggle,
    }] : []),
    ...(isStaff ? [{ key: 'mark-unread', label: t('ticketDetail.markUnread'), icon: <MailOpen size={16} strokeWidth={1.75} />, disabled: markUnreadMutation.isPending, onSelect: () => markUnreadMutation.mutate() }] : []),
    ...(mayForward ? [{ key: 'forward', label: t('ticketDetail.forward.action'), icon: <Send size={16} strokeWidth={1.75} className="rtl-mirror" />, onSelect: () => setShowForward(true) }] : []),
    ...(isStaff ? [{ key: 'link', label: t('ticketDetail.linkTask'), icon: <Link2 size={16} strokeWidth={1.75} />, onSelect: () => setRelationsSignal((n) => n + 1) }] : []),
    ...(isStaff && isProjectTask && ticket.project_id ? [{ key: 'milestone', label: t('ticketDetail.linkMilestone'), icon: <Flag size={16} strokeWidth={1.75} />, onSelect: () => setShowLinkMilestone(true) }] : []),
    ...(isStaff ? [{ key: 'move', label: t('ticketDetail.moveTicket'), icon: <Move size={16} strokeWidth={1.75} />, onSelect: () => setShowMoveModal(true) }] : []),
    ...(isStaff && !cancelled ? [{ key: 'cancel', label: isProjectTask ? t('ticketDetail.cancelTask') : t('ticketDetail.cancelTicket'), icon: <Ban size={16} strokeWidth={1.75} />, onSelect: () => setShowCancelTicket(true) }] : []),
    ...(isStaff && (!isProjectTask || ticket.can_manage_task === true) ? [{ key: 'delete', label: t('ticketDetail.delete'), icon: <Trash2 size={16} strokeWidth={1.75} />, danger: true, separatorBefore: true, onSelect: () => setShowDeleteTicket(true) }] : []),
  ];

  const closeLabel = isProjectTask ? t('ticketDetail.closeTask') : t('ticketDetail.closeTicket');
  // The one state-driven primary, as data: the header and the TopBar (PageChrome)
  // render the same handler with the same disabled / busy state.
  let primaryDef: PageChromeAction | null = null;
  if (closing) {
    primaryDef = {
      label: isProjectTask ? t('ticketDetail.closingTask') : t('ticketDetail.closingTicket'),
      icon: <CheckCircle2 size={16} aria-hidden="true" />, onClick: () => {}, disabled: true,
    };
  } else if (primary === 'start') {
    primaryDef = {
      label: t('ticketDetail.startWork'), icon: <Play size={16} aria-hidden="true" />,
      // FIX-D300-S2: with the switch on, a customer with no coverage - disabled (the banner says why and offers the request).
      onClick: () => startTimerMutation.mutate({ id: ticket.id, title: ticket.title }), disabled: startTimerMutation.isPending || ticket.coverage?.blocked === true, busy: startTimerMutation.isPending,
    };
  } else if (primary === 'stop' && myTimer) {
    primaryDef = {
      label: t('ticketDetail.stopAndLog'), icon: <Square size={14} className="fill-current" aria-hidden="true" />,
      onClick: () => setStopping(myTimer), extra: <ElapsedSince start={timerClockStart(myTimer)} />,
    };
  } else if (primary === 'close') {
    primaryDef = {
      label: closeLabel, icon: <CheckCircle2 size={16} aria-hidden="true" />,
      onClick: () => void close.request(closeItem), disabled: !!close.checking, busy: !!close.checking,
    };
  } else if (primary === 'reopen') {
    primaryDef = { label: t('ticketDetail.reopen'), icon: <RotateCcw size={16} aria-hidden="true" />, onClick: () => setShowReopen(true) };
  } else if (primary === 'uncancel') {
    primaryDef = {
      label: isProjectTask ? t('ticketDetail.uncancelTask') : t('ticketDetail.uncancelTicket'), icon: <RotateCcw size={16} aria-hidden="true" />,
      onClick: () => uncancelTicketMutation.mutate(), disabled: uncancelTicketMutation.isPending, busy: uncancelTicketMutation.isPending,
    };
  }
  const primaryButton: ReactNode = primaryDef ? (
    <button
      type="button"
      onClick={primaryDef.onClick}
      disabled={primaryDef.disabled}
      aria-busy={primaryDef.busy || undefined}
      aria-live={closing ? 'polite' : undefined}
      className="btn btn-primary"
    >
      {primaryDef.icon} {primaryDef.label}
      {primaryDef.extra}
    </button>
  ) : null;

  // D-317 §2 — a service ticket: the condensed header (name + status button; one line of facts) and the stretchable window.
  const compactHead = ticket.task_type === 'ticket';
  const statusControl: ReactNode = canChangeStatus ? (
    <TicketStatusPill
      status={shownStatus}
      taskType={ticket.task_type}
      cancelled={cancelled}
      pending={statusStepperMutation.isPending}
      onChange={(s) => statusStepperMutation.mutate(s)}
      onComplete={ticket.task_type === 'ticket' && primary === 'close' && !closing ? () => void close.request(closeItem) : undefined}
    />
  ) : (
    <StatusBadge status={ticket.status} taskType={ticket.task_type} />
  );

  const audience = audienceFor({
    viewerIsStaff: isStaff,
    taskType: ticket.task_type,
    customerName: ticket.customer_name,
    organizationName: ticket.organization_name,
  });

  // The ticket's facts and its assignees: in the side panel on wide screens,
  // in the folded "פרטים" row under the title below lg (rendered once, never both).
  const factsBlock = (
    <div className="p-5">
      {canChangeStatus ? (
        // FQ-045 (D-225 s.3): the status is changed from the pill next to the title (one control, not two).
        statusSelectLocked(shownStatus, cancelled) && (
          <p className="text-xs text-surface-500">
            {cancelled ? t('ticketDetail.statusLockedCancelled') : t('ticketDetail.statusLockedCompleted')}
          </p>
        )
      ) : (
        <div className="flex items-center justify-between gap-4">
          <span className="text-sm text-surface-600">{t('common.status')}</span>
          <StatusBadge status={ticket.status} taskType={ticket.task_type} />
        </div>
      )}
      <dl className="mt-3 divide-y divide-surface-100">
        <DetailRow label={t('common.priority')}>
          {pTone !== 'neutral'
            ? <Badge variant="outline" tone={pTone}>{getPriorityLabel(ticket.priority, t)}</Badge>
            : getPriorityLabel(ticket.priority, t)}
        </DetailRow>
        <DetailRow label={t('uxTickets.requester')}>{ticket.customer_name ? <bdi>{ticket.customer_name}</bdi> : t('common.notSpecified')}</DetailRow>
        {ticket.customer_approved_by_name && (
          <DetailRow label={t('tickets.customerApproval')}>
            {t('tickets.customerApprovedBy', {
              name: ticket.customer_approved_by_name,
              date: ticket.customer_approved_at ? formatDateTime(ticket.customer_approved_at) : '',
            })}
          </DetailRow>
        )}
        {ticket.organization_name && (
          <DetailRow label={t('tickets.organization')}>
            <OrgLink orgId={ticket.organization_id} name={ticket.organization_name} className="text-primary-700 hover:underline" />
          </DetailRow>
        )}
        {ticket.project_name && ticket.project_id && (
          <DetailRow label={t('tickets.project')}>
            <Link to={`/projects/${ticket.project_id}`} className="text-primary-700 hover:underline"><bdi>{ticket.project_name}</bdi></Link>
          </DetailRow>
        )}
        {!ticket.project_id && ticket.engagement_id && (
          <DetailRow label={t('tickets.engagement')}>
            <Link to={`/engagements/${ticket.engagement_id}`} className="text-primary-700 hover:underline"><bdi>{engagementLabelOf(ticket, t)}</bdi></Link>
          </DetailRow>
        )}
        {dueDate && (
          <DetailRow label={t('tickets.dueDate')}>
            <DeadlineBadge dueDate={dueDate} source={ticket.due_date_source} size="sm" />
          </DetailRow>
        )}
        <DetailRow label={t('uxTickets.colOpened')}><bdi className="tabular-nums">{formatDateTime(ticket.opened_at)}</bdi></DetailRow>
        {ticket.closed_at && (
          <DetailRow label={t('tickets.closedAt')}><bdi className="tabular-nums">{formatDateTime(ticket.closed_at)}</bdi></DetailRow>
        )}
      </dl>
      {isStaff && <TaskAuditLines task={ticket} className="mt-3 border-t border-surface-100 pt-3" />}
    </div>
  );
  // Assigned agents: one "שינוי" control opens add / remove
  const assigneesBlock = (
    <AssigneesBlock
      assigned={assignedAgents}
      primaryId={ticket.assigned_agent_id}
      agents={agents}
      canEdit={!!canEdit && isStaff && !archivedLock}
      pending={addAgentMutation.isPending || removeAgentMutation.isPending}
      onAdd={(agentId) => addAgentMutation.mutate(agentId)}
      onRemove={(agentId) => removeAgentMutation.mutate(agentId)}
      error={addAgentMutation.isError ? t('ticketDetail.addAgentError') : removeAgentMutation.isError ? t('ticketDetail.removeAgentError') : undefined}
    />
  );
  const ownerName = (assignedAgents.find((a) => a.id === ticket.assigned_agent_id) ?? assignedAgents[0])?.name;
  const factsSummary = [
    getStatusLabel(ticket.status, t),
    getPriorityLabel(ticket.priority, t),
    ownerName || t('tickets.unassigned'),
  ].join(' · ');

  return (
    <div className="max-w-7xl mx-auto">
      {/* PageChrome: title + the same primary in the TopBar once the h1 row scrolls away. */}
      <PageChrome title={ticket.title} action={primaryDef} />
      {/* Where this ticket lives ("חזרה" is the TopBar's, on every page) */}
      {ticket.project_name && ticket.project_id && (
        <nav aria-label={t('aria.breadcrumb')} className="flex items-center flex-wrap gap-x-1.5 gap-y-1 text-sm text-surface-600 mb-2 min-w-0">
          <Link to={`/projects/${ticket.project_id}`} className="hover:text-surface-900 truncate max-w-48">{ticket.project_name}</Link>
        </nav>
      )}
      {!ticket.project_id && ticket.engagement_id && (
        <nav aria-label={t('aria.breadcrumb')} className="flex items-center flex-wrap gap-x-1.5 gap-y-1 text-sm text-surface-600 mb-2 min-w-0">
          <Link to={`/engagements/${ticket.engagement_id}`} className="hover:text-surface-900 truncate max-w-48">{engagementLabelOf(ticket, t)}</Link>
        </nav>
      )}

      {/* Header: eyebrow, title, badges, one primary; "עריכה" appears on hover of the header */}
      <header className={`flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between mb-6 ${canEditDetails && !touch ? 'dt-row' : ''}`}>
        <div className="min-w-0">
          {!compactHead && (
          <p className="text-sm text-text-2 mb-1 flex flex-wrap items-center gap-x-1.5">
            {ticket.ticket_number ? <bdi dir="ltr" className="tabular-nums font-medium" translate="no">TKT-{ticket.ticket_number}</bdi> : null}
            {hasSourceIcon(ticket.source) && <><span aria-hidden="true">·</span><TicketSourceIcon source={ticket.source} withLabel /></>}
            {ticket.ticket_number && ticket.organization_name ? <span aria-hidden="true">·</span> : null}
            {ticket.organization_name && (
              <OrgLink orgId={ticket.organization_id} name={ticket.organization_name} className="hover:text-text hover:underline underline-offset-2" />
            )}
          </p>
          )}
          <div className={compactHead ? 'flex flex-wrap items-center gap-x-2.5 gap-y-1' : 'flex items-start gap-2'}>
            <h1 className="text-2xl font-semibold tracking-tight text-surface-900 break-words min-w-0"><bdi>{ticket.title}</bdi></h1>
            {/* D-317 §2/§5 — a service ticket: the status button sits next to its name. */}
            {compactHead && statusControl}
            {canEditDetails && !touch && (
              <button type="button" onClick={openEdit} className="dt-reveal btn btn-ghost btn-sm mt-0.5 shrink-0 text-text-2">
                <Pencil size={14} aria-hidden="true" /> {t('ticketDetail.edit')}
              </button>
            )}
          </div>
          {/* D-255: who is working on it right now (live with the 5s ticket poll). */}
          <WorkingNowBanner items={ticket.working_now} />
          <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1.5 text-sm text-surface-600" data-testid={compactHead ? 'ticket-meta-line' : undefined}>
            {compactHead ? (
              // D-317 §2 — ONE line under the name: number · source · customer · priority · opened · first reply by.
              <>
                {ticket.ticket_number ? <bdi dir="ltr" className="tabular-nums font-medium" translate="no">TKT-{ticket.ticket_number}</bdi> : null}
                {hasSourceIcon(ticket.source) && <><span aria-hidden="true">·</span><TicketSourceIcon source={ticket.source} withLabel /></>}
                {ticket.organization_name && (
                  <><span aria-hidden="true">·</span><OrgLink orgId={ticket.organization_id} name={ticket.organization_name} className="hover:text-text hover:underline underline-offset-2" /></>
                )}
                <span aria-hidden="true">·</span>
              </>
            ) : statusControl}
            {statusStepperMutation.isError && <span className="text-danger-700">{t('ticketDetail.statusChangeFailed')}</span>}
            {/* C-32 (clarity, D-22): the priority tag is outlined, never filled like a status. */}
            {pTone !== 'neutral' && <Badge variant="outline" tone={pTone}>{getPriorityLabel(ticket.priority, t)}</Badge>}
            {isProjectTask && <Badge tone="neutral">{t('tickets.task')}</Badge>}
            {ticket.task_type === 'discussion' && <Badge tone="neutral">{t(ticket.is_conversation ? 'discussions.tagConversation' : 'taskType.discussion')}</Badge>}
            {cancelled && <Badge tone="neutral">{t('tickets.cancelledLabel')}</Badge>}
            {ticket.bug_report_context_id && <BugReportBadge />}
            {compactHead && pTone === 'neutral' && <span>{t('common.priority')} {getPriorityLabel(ticket.priority, t)}</span>}
            <span className="text-surface-400" aria-hidden="true">·</span>
            <span>{t('uxTickets.colOpened')} <bdi className="tabular-nums">{formatDateTime(ticket.opened_at)}</bdi></span>
            {compactHead && ticket.sla_response_due_at && !ticket.first_response_at && (
              <>
                <span className="text-surface-400" aria-hidden="true">·</span>
                <span>{t('ticketDetail.firstReplyBy', { time: formatDateTime(ticket.sla_response_due_at) })}</span>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap shrink-0">
          {moreActions.length > 0 && !archivedLock && (
            <MenuButton
              items={moreActions}
              label={t('uxTickets.moreActions')}
              triggerClassName="btn btn-secondary px-2.5"
              showTooltip
              triggerProps={{ 'data-ticket-more': '' }}
            >
              <MoreHorizontal size={18} aria-hidden="true" />
            </MenuButton>
          )}
          {/* D7: a project task logs time from its own page. */}
          {isStaff && isProjectTask && !cancelled && !archivedLock && (
            <button type="button" onClick={() => setLogTimeFor(ticket)} className="btn btn-secondary">
              <Clock size={16} aria-hidden="true" /> {t('ticketDetail.logTime')}
            </button>
          )}
          {!archivedLock && primaryButton}
        </div>
      </header>
      {archivedLock && (
        <div role="status" data-testid="ticket-engagement-archived-banner" className="mb-4 rounded-md border border-warning-300 bg-warning-50 p-3 text-sm text-surface-900">
          {t('engagements.archivedBannerNoDate')}
        </div>
      )}

      {/* Below lg: the facts fold under the title instead of waiting after the whole conversation. */}
      {!wide && !isProjectTask && !editing && (
        <section className="-mt-2 mb-6 rounded-xl border border-surface-200 bg-surface-0" aria-labelledby="td-facts-toggle">
          <h2 className="text-sm">
            <button
              id="td-facts-toggle"
              type="button"
              onClick={() => setFactsOpen((v) => !v)}
              aria-expanded={factsOpen}
              aria-controls="td-facts"
              className="flex min-h-touch w-full items-center gap-2 rounded-xl px-4 py-2 text-start focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus"
            >
              <span className="shrink-0 font-semibold text-text">{t('ticketDetail.factsToggle')}</span>
              <span className="min-w-0 flex-1 truncate font-normal text-text-2"><bdi>{factsSummary}</bdi></span>
              <ChevronDown size={18} className={`shrink-0 text-text-3 transition-transform duration-micro ${factsOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
            </button>
          </h2>
          <div id="td-facts" hidden={!factsOpen} className="divide-y divide-surface-200 border-t border-surface-200">
            {factsBlock}
            {assigneesBlock}
          </div>
        </section>
      )}

      <div className="space-y-3 mb-6 empty:hidden">
        {/* §18 — cancelled banner (cancellation is separate from status) */}
        {cancelled && (
          <div className="flex items-start gap-3 bg-surface-100 text-surface-800 px-4 py-3 rounded-lg" role="status">
            <Ban size={18} className="mt-0.5 shrink-0 text-surface-600" aria-hidden="true" />
            <div className="min-w-0 text-sm">
              <p className="font-medium">{isProjectTask ? t('ticketDetail.cancelledBannerTask') : t('ticketDetail.cancelledBannerTicket')} · <bdi className="font-normal text-surface-600 tabular-nums">{formatDateTime(ticket.cancelled_at!)}</bdi></p>
              {ticket.cancel_reason && (
                <p className="mt-1"><span className="text-surface-600">{t('tickets.cancelReason')}: </span><bdi>{ticket.cancel_reason}</bdi></p>
              )}
            </div>
          </div>
        )}
        {/* P-43 (D-213) — a request that arrived with no customer: the one place to give it one. */}
        {showAssignCustomer(ticket, isStaff) && (
          <div className="flex flex-wrap items-center gap-3 bg-warning-50 text-warning-800 px-4 py-3 rounded-lg" role="status">
            <AlertTriangle size={18} className="shrink-0" aria-hidden="true" />
            <p className="min-w-0 flex-1 text-sm font-medium">{t('ticketDetail.assignCustomer.banner')}</p>
            <button type="button" className="btn btn-primary" onClick={() => setAssignOpen(true)}>
              {t('ticketDetail.assignCustomer.action')}
            </button>
          </div>
        )}
        {/* FIX-D300-S2 (D-300 sec.2-4) — a customer with no warranty / bank / contract (staff, service requests). */}
        {isStaff && ticket.task_type === 'ticket' && <TicketCoverageBanner ticketId={ticket.id} coverage={ticket.coverage} />}
        <DeadlineBanner
          dueDate={ticket.effective_due_date || ticket.due_date}
          source={ticket.due_date_source}
          entityLabel={t('deadline.theTicket')}
        />
        {(startTimerMutation.isError || statusStepperMutation.isError) && (
          // D-285 — leaves by itself after ~4 s; ✕ closes it.
          <TransientAlert
            message={apiErrorMessage(startTimerMutation.error ?? statusStepperMutation.error) || t('ticketDetail.actionFailed')}
            onClose={() => { startTimerMutation.reset(); statusStepperMutation.reset(); }}
            className="bg-danger-50 text-danger-700 px-4 py-3 rounded-lg text-sm"
          >
            <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
            {apiErrorMessage(startTimerMutation.error ?? statusStepperMutation.error) || t('ticketDetail.actionFailed')}
          </TransientAlert>
        )}
      </div>

      {/* Regular ticket view (task_type !== 'task') */}
      {!isProjectTask && (editing ? (
        <div className="max-w-2xl space-y-4">
          <EditTicketForm
            ticket={ticket}
            agents={agents}
            canChangeStatus={!!canChangeStatus}
            onClose={() => setEditing(false)}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: ['ticket', id] });
              queryClient.invalidateQueries({ queryKey: ['ticket-timeline', id] });
              setEditing(false);
              toast('success', t('ticketDetail.saved'));
            }}
          />
          <TicketAttachmentsCard
            compact
            attachments={allAttachments}
            ticketId={id!}
            userId={user?.id || ''}
            userRole={user?.role || ''}
            onSuccess={() => queryClient.invalidateQueries({ queryKey: ['ticket-timeline', id] })}
          />
        </div>
      ) : (
        <div
          ref={paneGridRef}
          className={compactHead ? 'relative grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_var(--td-side)] gap-6 items-start' : 'grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_20rem] gap-6 items-start'}
          style={compactHead ? ({ '--td-side': `${sideW}px` } as React.CSSProperties) : undefined}
        >
          {/* D-317 §1 — the line between the conversation and the side panel: drag (or arrows) to stretch; kept per user. */}
          {compactHead && (
            <button
              type="button"
              aria-label={t('ticketDetail.pane.resize')}
              title={t('ticketDetail.pane.resize')}
              data-testid="ticket-pane-resize"
              className="absolute inset-y-0 z-10 hidden w-2 cursor-col-resize rounded-full hover:bg-surface-200 focus-visible:bg-surface-200 focus-visible:outline-none lg:block end-[calc(var(--td-side)+0.5rem)]"
              onPointerDown={(e) => {
                e.preventDefault();
                (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
              }}
              onPointerMove={(e) => {
                if (!(e.currentTarget as HTMLElement).hasPointerCapture?.(e.pointerId)) return;
                const r = paneGridRef.current?.getBoundingClientRect();
                if (r) setSideW(sideWidthFromPointer({ clientX: e.clientX, gridLeft: r.left, gridRight: r.right, rtl: document.documentElement.dir === 'rtl' }));
              }}
              onPointerUp={() => writeSideWidth(paneStore(), user?.id, sideWRef.current)}
              onKeyDown={(e) => {
                const next = sideWidthFromKey(sideW, e.key, document.documentElement.dir === 'rtl');
                if (next === null) return;
                e.preventDefault();
                setSideW(next);
                writeSideWidth(paneStore(), user?.id, next);
              }}
            />
          )}
          {/* Main: description + conversation and records */}
          <div className="min-w-0 space-y-6">
            {/* D-341 (5): the report's treatment panel comes FIRST on a bug report (it used to sit below the whole ticket). */}
            {ticket.bug_report_context_id && isStaff && <BugTreatmentPanel ticketId={id!} />}
            <section
              className={`rounded-xl border border-surface-200 bg-surface-0 ${compactHead && !paneFull ? 'relative' : ''} ${paneFull ? 'fixed inset-2 z-popover overflow-auto shadow-elevated md:inset-4' : ''}`}
              aria-label={t('tickets.details')}
              data-testid={compactHead ? 'ticket-window' : undefined}
              // QA-D316-317 (390): full screen covers the tab bar - the docked composer sits at the window's bottom.
              style={paneFull ? ({ '--bottom-chrome': '0px' } as React.CSSProperties) : undefined}
            >
              {compactHead && (
                <div className="absolute top-3 end-3 z-10 flex gap-1">
                  {paneFull ? (
                    <button type="button" className="btn-icon size-8 rounded-md border border-surface-200 bg-surface-0" onClick={() => setPaneFull(false)} aria-label={t('ticketDetail.pane.restore')} title={t('ticketDetail.pane.restore')}>
                      <Minimize2 size={16} aria-hidden="true" />
                    </button>
                  ) : (
                    <button type="button" className="btn-icon size-8 rounded-md border border-surface-200 bg-surface-0" onClick={() => setPaneFull(true)} aria-label={t('ticketDetail.pane.fullScreen')} title={t('ticketDetail.pane.fullScreen')}>
                      <Maximize2 size={16} aria-hidden="true" />
                    </button>
                  )}
                </div>
              )}
              {/* QA-D316-317 #1 — full screen covers the page header: the name and the status button come with it. */}
              {paneFull && (
                <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 border-b border-surface-200 px-5 pt-4 pb-3 pe-16" data-testid="ticket-window-head">
                  <p className="text-lg font-semibold text-surface-900 break-words min-w-0"><bdi>{ticket.title}</bdi></p>
                  {statusControl}
                </div>
              )}
              <div className="px-5 pt-5 pb-4">
                <h2 className="text-xs font-medium text-surface-600 mb-1.5">{t('common.description')}</h2>
                {ticket.description ? (
                  // Each line takes its own direction (unicode-bidi: plaintext), so an English
                  // paragraph inside a Hebrew description is not reordered.
                  <p className="text-body-long text-surface-900 whitespace-pre-wrap break-words max-w-3xl text-start [unicode-bidi:plaintext]">{ticket.description}</p>
                ) : (
                  <p className="text-sm text-surface-500">{t('common.notSpecified')}</p>
                )}
                {/* D-317 §3 — the files of the opening as uniform tiles (they were the first chat bubble). */}
                {compactHead && openingFiles.length > 0 && (
                  <div className="mt-3">
                    <p className="mb-1.5 text-xs font-medium text-surface-600">{t('ticketDetail.openingFiles', { n: openingFiles.length })}</p>
                    <AttachmentTiles files={openingFiles} label={t('ticketDetail.openingFiles', { n: openingFiles.length })} />
                  </div>
                )}
              </div>
              <Tabs
                items={detailTabs}
                value={activeTab}
                onChange={setActiveTab}
                ariaLabel={t('tickets.details')}
                className="px-3 border-t"
              />
              <div role="tabpanel" aria-label={detailTabs.find((d) => d.key === activeTab)?.label as string}>
                {activeTab === 'chat' && (
                  // Phones: the conversation flows with the page and its composer docks to the
                  // bottom of the screen (above the tab bar); the fixed-height pane is md+ only.
                  // C-33 (clarity, N-02): on md+ the pane reaches the bottom of the screen at load
                  // (its top is measured into --chat-top, the floating "+" row is left clear), so the
                  // composer is visible without scrolling the page: one scroll axis, the conversation's.
                  <div ref={chatPaneRef} className="relative md:h-[max(26rem,calc(100dvh-var(--chat-top,20rem)-var(--fab-clearance)))] flex flex-col rounded-b-xl overflow-hidden max-md:h-auto max-md:min-h-0 max-md:overflow-visible">
                    {timelineLoading && <div className="spinner w-4 h-4 absolute top-3 end-3 z-10" aria-hidden="true" />}
                    <TicketChatView dockComposer readOnly={archivedLock} timeline={timeline} ticketId={id!} audience={audience} taskType={ticket.task_type}
                      opening={{ files: compactHead ? [] : openingFiles, description: ticket.description, authorName: ticket.customer_name, createdAt: ticket.created_at }}
                      isConversation={ticket.is_conversation === true}
                      hasCustomer={(ticket.extra_customers?.length ?? 0) > 0 || ticket.organization_is_internal === false}
                      replyBar={isStaff && ticket.task_type === 'ticket' ? { title: ticket.title, status: shownStatus, cancelled, onCreateTask: () => setCreateTaskFromTicket(true), onForward: mayForward ? () => setShowForward(true) : undefined } : undefined}
                      className="h-full min-h-0 flex flex-col overflow-hidden max-md:overflow-visible rounded-b-xl" />
                  </div>
                )}
                {activeTab === 'subtasks' && showSubtasksTab && (
                  <div className="p-5"><SubtasksSection ticketId={id!} isParent={isParentTicket} /></div>
                )}
                {activeTab === 'worklogs' && canSeeInternal && (
                  <div className="p-5">
                    <WorkLogsCard
                      workLogs={workLogs}
                      canAddLog={!!canAddLog}
                      ticketId={id!}
                      onAddSuccess={() => queryClient.invalidateQueries({ queryKey: ['work-logs', { ticket_id: id }] })}
                      move={ticket.engagement_id && !archivedLock ? { organizationId: ticket.organization_id, engagementId: ticket.engagement_id, title: ticket.title } : undefined}
                    />
                  </div>
                )}
                {activeTab === 'attachments' && (
                  <div className="p-5">
                    <TicketAttachmentsCard
                      tabMode
                      attachments={allAttachments}
                      ticketId={id!}
                      userId={user?.id || ''}
                      userRole={user?.role || ''}
                      onSuccess={() => queryClient.invalidateQueries({ queryKey: ['ticket-timeline', id] })}
                    />
                  </div>
                )}
              </div>
            </section>

            {ticket.bug_report_context_id && isStaff && (
              <BugReportContextPanel ticketId={id!} />
            )}
          </div>

          {/* Side panel: the ticket's facts, one consistent definition list */}
          <aside className="rounded-xl border border-surface-200 bg-surface-0 divide-y divide-surface-200" aria-label={t('uxTickets.details')}>
            {wide && factsBlock}
            {wide && assigneesBlock}

            {/* SLA — neutral unless at risk; the pause action lives in ⋯, the paused state shows here */}
            {hasSla && (
              <div className="p-5">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <h2 className="text-sm font-semibold text-surface-900">{t('sla.title')}</h2>
                  {sla.paused && <Badge tone="warning">{t('ticketDetail.slaPausedBadge')}</Badge>}
                </div>
                <SlaBadge
                  responseDueAt={ticket.sla_response_due_at}
                  resolutionDueAt={ticket.sla_resolution_due_at}
                  responseBreached={ticket.sla_response_breached}
                  resolutionBreached={ticket.sla_resolution_breached}
                  firstResponseAt={ticket.first_response_at}
                  responseWarningSentAt={ticket.sla_response_warning_sent_at}
                  resolutionWarningSentAt={ticket.sla_resolution_warning_sent_at}
                  status={ticket.status}
                />
              </div>
            )}

            {/* Time */}
            <div className="p-5">
              <h2 className="text-sm font-semibold text-surface-900 mb-3">{t('tickets.timeSummary')}</h2>
              <dl className="grid grid-cols-3 gap-3">
                <div>
                  <dt className="text-xs text-surface-600">{t('tickets.timeSummaryTotal')}</dt>
                  <dd className="text-base font-semibold text-surface-900 tabular-nums">{formatNumber(totalMinutes / 60, 1)} <span className="text-xs font-normal text-surface-600">{t('common.hoursShort')}</span></dd>
                </div>
                <div>
                  <dt className="text-xs text-surface-600">{t('tickets.timeSummaryBillable')}</dt>
                  <dd className="text-base font-semibold text-surface-900 tabular-nums">{formatNumber(billableMinutes / 60, 1)} <span className="text-xs font-normal text-surface-600">{t('common.hoursShort')}</span></dd>
                </div>
                <div>
                  <dt className="text-xs text-surface-600">{t('tickets.timeSummaryRecords')}</dt>
                  <dd className="text-base font-semibold text-surface-900 tabular-nums">{workLogs.length}</dd>
                </div>
              </dl>
              {(ticket.estimated_hours != null || Number(ticket.actual_hours) > 0) && (
                <div className="mt-4">
                  <p className="text-xs text-surface-600 mb-1.5">{t('tickets.estimatedVsActual')}</p>
                  {ticket.estimated_hours != null && Number(ticket.estimated_hours) > 0 ? (
                    <HoursProgressBar actual={Number(ticket.actual_hours)} estimated={Number(ticket.estimated_hours)} />
                  ) : (
                    <p className="text-sm text-surface-700 tabular-nums">{formatNumber(Number(ticket.actual_hours), 1)} {t('tickets.actualWithoutEstimate')}</p>
                  )}
                </div>
              )}
            </div>

            {ticket.customer_summary && (isCustomerRole || isAdmin) && (
              <div className="p-5">
                <h2 className="text-sm font-semibold text-surface-900 mb-1.5">{t('tickets.customerSummary')}</h2>
                <p className="text-sm text-surface-700 whitespace-pre-wrap break-words"><bdi>{ticket.customer_summary}</bdi></p>
              </div>
            )}
          </aside>
        </div>
      ))}

      {/* P-37 (Q-179) — the tasks created from this ticket (both ways). */}
      {canSeeInternal && (
        <TicketTaskLinks className="mt-6" sourceTicket={ticket.source_ticket} createdTasks={ticket.created_tasks} />
      )}

      {/* B.17 — manual task relations (staff): the panel steps aside while empty; ⋯ "קישור למשימה" opens it */}
      {canSeeInternal && (
        <div className="mt-6 empty:hidden">
          <TaskRelationsPanel ticketId={id!} canEdit={isStaff && !archivedLock} hideWhenEmpty addSignal={relationsSignal} />
        </div>
      )}

      {assignOpen && (
        <AssignCustomerDialog
          ticketId={id!}
          ticketTitle={ticket.title}
          onClose={() => setAssignOpen(false)}
        />
      )}

      {showForward && (
        <ForwardTicketDialog
          ticketId={id!}
          ticketTitle={ticket.title}
          agents={agents}
          currentUserId={user?.id}
          onClose={() => setShowForward(false)}
        />
      )}

      {showMoveModal && (
        <MoveTicketModal
          ticketId={id!}
          currentProjectId={ticket.project_id}
          currentMilestoneId={ticket.milestones?.[0]?.id}
          onClose={() => setShowMoveModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['ticket', id] });
            setShowMoveModal(false);
            toast('success', t('ticketDetail.moved'));
          }}
        />
      )}

      {/* B.23 — explicit reopen (optional reason, audited) */}
      {showReopen && (
        <ReopenDialog
          isTask={isProjectTask}
          pending={reopenTicketMutation.isPending}
          error={reopenTicketMutation.error}
          onConfirm={(reason) => reopenTicketMutation.mutate(reason)}
          onClose={() => setShowReopen(false)}
        />
      )}

      {/* Delete confirmation */}
      {showDeleteTicket && (
        <ConfirmDeleteModal
          title={isProjectTask ? t('ticketDetail.deleteTaskTitle') : t('ticketDetail.deleteTicketTitle')}
          itemName={ticket.title}
          onConfirm={() => {
            setShowDeleteTicket(false);
            requestDeleteTicket();
          }}
          onCancel={() => setShowDeleteTicket(false)}
        />
      )}

      {hasWorkMinutes !== null && (
        <TaskHasWorkDialog
          minutes={hasWorkMinutes}
          onCancelTask={() => { setHasWorkMinutes(null); setShowCancelTicket(true); }}
          onClose={() => setHasWorkMinutes(null)}
        />
      )}

      {ticketHasWorkMinutesState !== null && (
        <TicketHasWorkDialog
          minutes={ticketHasWorkMinutesState}
          onCloseTicket={isStaff && (primary === 'start' || primary === 'stop')
            ? () => { setTicketHasWorkMinutes(null); void close.request(closeItem); }
            : undefined}
          onClose={() => setTicketHasWorkMinutes(null)}
        />
      )}

      {/* §18 — cancel (reason required) */}
      {showCancelTicket && (
        <CancelTicketDialog
          isTask={isProjectTask}
          pending={cancelTicketMutation.isPending}
          error={cancelTicketMutation.error}
          onConfirm={(reason) => cancelTicketMutation.mutate(reason)}
          onClose={() => setShowCancelTicket(false)}
        />
      )}

      {showLinkMilestone && ticket.project_id && (
        <LinkMilestoneModal
          ticketId={id!}
          projectId={ticket.project_id}
          currentMilestoneId={ticket.milestones?.[0]?.id}
          onClose={() => setShowLinkMilestone(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['ticket', id] });
            queryClient.invalidateQueries({ queryKey: ['project', ticket.project_id] });
            setShowLinkMilestone(false);
            toast('success', t('ticketDetail.linkMilestoneDone'));
          }}
        />
      )}

      {createTaskFromTicket && (
        <TaskFormModal
          mode="create"
          placement={{ organizationId: ticket.organization_id || undefined, engagementId: ticket.engagement_id || undefined }}
          fromTicket={{
            id: ticket.id,
            title: ticket.title,
            description: ticket.description,
            filesCount: ticketAttachments.length,
          }}
          onClose={() => setCreateTaskFromTicket(false)}
          onCreated={(taskId) => {
            setCreateTaskFromTicket(false);
            queryClient.invalidateQueries({ queryKey: ['ticket', id] });
            queryClient.invalidateQueries({ queryKey: ['all-tasks'] });
            toast('success', t('replyTimer.taskCreated'), { action: { label: t('replyTimer.openTask'), onClick: () => navigate(`/tasks/${taskId}`) } });
          }}
        />
      )}

      {logTimeFor && (
        <NewWorkLogModal
          initialTab="tasks"
          initialItem={{ id: logTimeFor.id, title: logTimeFor.title } as Ticket}
          onClose={() => setLogTimeFor(null)}
          onSuccess={() => {
            setLogTimeFor(null);
            queryClient.invalidateQueries({ queryKey: ['work-logs', { ticket_id: id }] });
            queryClient.invalidateQueries({ queryKey: ['ticket', id] });
            toast('success', t('quickCreate.workLogCreated'));
          }}
        />
      )}

      {stopping && (
        <TimerSummaryModal
          target={{ ...timerTargetFromTimer(stopping), taskType: ticket.task_type, parentTicketId: ticket.parent_ticket_id ?? null }}
          onClose={() => setStopping(null)}
          onDone={() => {
            setStopping(null);
            queryClient.invalidateQueries({ queryKey: ['timer-status'] });
            queryClient.invalidateQueries({ queryKey: ['work-logs'] });
            queryClient.invalidateQueries({ queryKey: ['work-records'] });
            queryClient.invalidateQueries({ queryKey: ['ticket', id] });
          }}
        />
      )}

      {close.dialogs}
    </div>
  );
}

/** The running time on the primary "עצירה ורישום" (h:mm:ss, updated each second). */
function ElapsedSince({ start }: { start: string }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, []);
  const secs = Math.max(0, Math.floor((now - new Date(start).getTime()) / 1000));
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  const text = `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return <span className="ms-1 tabular-nums opacity-90" dir="ltr" aria-hidden="true">{text}</span>;
}

/**
 * Status pill (B.23, FQ-045 / D-225 s.3): one pill that opens the list of statuses; never offers "הושלם"
 * (closing is the primary, `useCloseTicket`); read-only while completed or cancelled (reopen / uncancel first).
 */
function TicketStatusPill({ status, taskType, cancelled, pending, onChange, onComplete }: {
  status: string;
  /** D-146 - a service request offers its own statuses. */
  taskType?: string | null;
  cancelled: boolean;
  pending: boolean;
  onChange: (status: TicketStatus) => void;
  /** D-317 §5 - a service request: "הושלמה" from the list = the existing close ("סיום עבודה"). */
  onComplete?: () => void;
}) {
  const { t } = useTranslation();
  const { labelOf } = useTaskStatuses();
  const locked = statusSelectLocked(status, cancelled);
  // D-317 §5 (D-146) - a service request lists all 5 statuses: "הושלמה" closes it the usual way;
  // "נסגרה (באישור)" is shown only (the customer's approval / 7 days, D-146).
  const ticketList = taskType === 'ticket' && !locked;
  const keys = locked ? [status] : ticketList ? [...statusSelectOptions(status, taskType), 'completed', 'closed'] : statusSelectOptions(status, taskType);
  const options: StatusPillOption<string>[] = keys.map((k) => ({
    value: k,
    label: ticketList && k === 'closed' ? t('ticketDetail.statusClosedByApproval') : (labelOf(k, taskType) || getStatusLabel(k as TicketStatus, t, taskType)),
    tone: statusTone(k),
    ...(ticketList && k === 'completed' ? { separatorBefore: true, disabled: !onComplete } : {}),
    ...(ticketList && k === 'closed' ? { disabled: true } : {}),
  }));
  const current = options.find((o) => o.value === status)?.label ?? status;
  return (
    <StatusPillSelect<string>
      value={status}
      options={options}
      onChange={(s) => { if (ticketList && s === 'completed') onComplete?.(); else onChange(s as TicketStatus); }}
      disabled={locked}
      pending={pending}
      label={`${t('common.status')}: ${current}`}
      sheetTitle={t('common.status')}
      prefix={taskType === 'ticket' ? t('common.status') : undefined}
    />
  );
}

/** Assigned agents, with one "שינוי" control (popover with add / remove) instead of an add button plus a remove per person. */
function AssigneesBlock({ assigned, primaryId, agents, canEdit, pending, onAdd, onRemove, error }: {
  assigned: TicketAgent[];
  /** The owner ("אחראי"); everyone else on the list is an additional assignee ("משויכים נוספים"). */
  primaryId?: string | null;
  agents: { id: string; name: string }[];
  canEdit: boolean;
  pending: boolean;
  onAdd: (id: string) => void;
  onRemove: (id: string) => void;
  error?: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLButtonElement>(null);
  const assignedIds = new Set(assigned.map((a) => a.id));
  return (
    <div className="p-5">
      <div className="flex items-center justify-between gap-2 mb-2">
        <h2 id="td-agents-title" className="text-sm font-semibold text-surface-900">{t('tickets.assignedAgents')}</h2>
        {canEdit && (
          <button
            ref={anchor}
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-haspopup="dialog"
            className="btn btn-ghost btn-sm -me-2"
          >
            <Users size={14} aria-hidden="true" /> {t('ticketDetail.changeAgents')}
          </button>
        )}
      </div>
      {assigned.length > 0 ? (
        <ul className="space-y-1" aria-labelledby="td-agents-title">
          {[...assigned].sort((a, b) => Number(b.id === primaryId) - Number(a.id === primaryId)).map((agent) => (
            <li key={agent.id} className="flex items-center gap-2 min-h-9">
              <span className="avatar w-7 h-7 text-xs shrink-0" aria-hidden="true">{agent.name?.trim().charAt(0)}</span>
              <bdi className="flex-1 min-w-0 truncate text-sm text-surface-900">{agent.name}</bdi>
              {agent.id === primaryId && <span className="shrink-0 text-xs text-text-2">{t('ticketDetail.ownerTag')}</span>}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-surface-600">{t('tickets.noAgentsAssigned')}</p>
      )}
      {error && <p className="error-text" role="alert">{error}</p>}
      {canEdit && (
        <Popover
          open={open}
          onClose={() => setOpen(false)}
          anchorRef={anchor}
          role="dialog"
          ariaLabel={t('ticketDetail.changeAgents')}
          focusFirst
          className="w-72 p-2"
        >
          <fieldset>
            <legend className="px-2 pb-1.5 pt-1 text-xs font-medium text-text-2">{t('ticketDetail.agentsLegend')}</legend>
            {agents.length === 0 ? (
              <p className="px-2 py-2 text-sm text-text-2">{t('ticketDetail.noAgents')}</p>
            ) : (
              <ul className="max-h-72 overflow-y-auto">
                {agents.map((a) => {
                  const on = assignedIds.has(a.id);
                  return (
                    <li key={a.id}>
                      <label className="flex min-h-9 cursor-pointer items-center gap-2.5 rounded-lg px-2 text-sm text-text hover:bg-surface-subtle">
                        <input
                          type="checkbox"
                          className="size-4 accent-primary-700"
                          checked={on}
                          disabled={pending}
                          onChange={() => (on ? onRemove(a.id) : onAdd(a.id))}
                        />
                        <bdi className="min-w-0 flex-1 truncate">{a.name}</bdi>
                        {on && <Check size={14} className="text-accent-text" aria-hidden="true" />}
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </fieldset>
        </Popover>
      )}
    </div>
  );
}

export function ReopenDialog({ isTask, pending, error, onConfirm, onClose }: {
  isTask: boolean;
  pending: boolean;
  error: unknown;
  onConfirm: (reason: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  return (
    <Dialog
      open
      onClose={onClose}
      title={isTask ? t('ticketDetail.reopenTitleTask') : t('ticketDetail.reopenTitleTicket')}
      description={isTask ? t('ticketDetail.reopenHelpTask') : t('ticketDetail.reopenHelpTicket')}
      size="md"
      dismissible={!pending}
      footer={(
        <>
          <button type="button" onClick={onClose} className="btn btn-secondary" disabled={pending}>{t('common.cancel')}</button>
          <SubmitButton form="reopen-form" busy={pending}>{t('ticketDetail.reopen')}</SubmitButton>
        </>
      )}
    >
      <form id="reopen-form" onSubmit={(e) => { e.preventDefault(); onConfirm(reason.trim()); }} className="space-y-3">
        <ServerError error={error} fallback={t('ticketDetail.reopenFailed')} />
        <Field label={t('ticketDetail.reopenReason')} id="reopen-reason" optional>
          <textarea className="input min-h-20" value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
      </form>
    </Dialog>
  );
}

export function CancelTicketDialog({ isTask, pending, error, onConfirm, onClose }: {
  isTask: boolean;
  pending: boolean;
  error: unknown;
  onConfirm: (reason: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [fieldError, setFieldError] = useState<string | undefined>();
  const check = (v: string) => (v.trim().length < 2 ? t('tickets.cancelReasonMin') : undefined);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const err = check(reason);
    setFieldError(err);
    if (err) { focusField('cancel-reason'); return; }
    onConfirm(reason.trim());
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={isTask ? t('ticketDetail.cancelTaskTitle') : t('ticketDetail.cancelTicketTitle')}
      description={isTask ? t('ticketDetail.cancelHelpTask') : t('ticketDetail.cancelHelpTicket')}
      size="md"
      dismissible={!pending}
      footer={(
        <>
          <button type="button" onClick={onClose} className="btn btn-secondary" disabled={pending}>{t('ticketDetail.keepOpen')}</button>
          <button type="submit" form="cancel-ticket-form" className="btn btn-danger" disabled={pending} aria-busy={pending || undefined}>
            {isTask ? t('ticketDetail.cancelTask') : t('ticketDetail.cancelTicket')}
          </button>
        </>
      )}
    >
      <form id="cancel-ticket-form" onSubmit={submit} noValidate className="space-y-3">
        <ServerError error={error} fallback={t('ticketDetail.cancelFailed')} />
        <Field label={t('tickets.cancelReason')} id="cancel-reason" required error={fieldError}>
          <textarea
            className="input min-h-20"
            value={reason}
            onChange={(e) => { setReason(e.target.value); if (fieldError) setFieldError(check(e.target.value)); }}
            onBlur={() => setFieldError(reason ? check(reason) : undefined)}
            placeholder={t('ticketDetail.cancelReasonPlaceholder')}
          />
        </Field>
      </form>
    </Dialog>
  );
}

/** P-02b — a task with no project shows its own engagement: "name · type". */
function engagementLabelOf(ticket: Ticket, t: (key: string) => string): string {
  return engagementLabel(ticket.engagement_name, ticket.engagement_type, t) || '—';
}

/** One row of the side panel's definition list: label at the start, value at the end. */
function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-2.5">
      <dt className="text-sm text-surface-600 shrink-0">{label}</dt>
      <dd className="text-sm text-surface-900 text-end min-w-0 break-words">{children}</dd>
    </div>
  );
}

// ============================================================
// EditTicketForm
// ============================================================
function EditTicketForm({ ticket, agents, canChangeStatus, onClose, onSuccess }: {
  ticket: Ticket;
  agents: any[];
  canChangeStatus: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const [title, setTitle] = useState(ticket.title);
  const [description, setDescription] = useState(ticket.description || '');
  const [priority, setPriority] = useState(ticket.priority);
  const [status, setStatus] = useState(ticket.status);
  const [agentId, setAgentId] = useState(ticket.assigned_agent_id || '');
  const [estimatedHours, setEstimatedHours] = useState(ticket.estimated_hours?.toString() || '');
  const [customerSummary, setCustomerSummary] = useState(ticket.customer_summary || '');
  const [dueDate, setDueDate] = useState(ticket.due_date ? ticket.due_date.slice(0, 10) : '');
  const [titleError, setTitleError] = useState<string | undefined>();
  const [submitted, setSubmitted] = useState(false);
  const checkTitle = (v: string) => (v.trim().length < 3 ? t('ticketDetail.titleTooShort') : undefined);
  const { labelOf } = useTaskStatuses();
  const statusLocked = statusSelectLocked(ticket.status, !!ticket.cancelled_at);
  const statusOptions = statusLocked ? [ticket.status] : statusSelectOptions(ticket.status, ticket.task_type);

  const mutation = useMutation({
    mutationFn: () => api.updateTicket(ticket.id, {
      title, description, priority, status: canChangeStatus ? status : undefined,
      assigned_agent_id: agentId || null,
      estimated_hours: estimatedHours ? parseFloat(estimatedHours) : null, // D-328: blank clears (NULL)
      customer_summary: customerSummary || undefined,
      due_date: dueDate || null,
    } as any),
    onSuccess,
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const err = checkTitle(title);
    setTitleError(err);
    setSubmitted(true);
    if (err) { focusField('et-title'); return; }
    mutation.mutate();
  };

  return (
    <section className="card" aria-labelledby="et-heading">
      <div className="flex items-center justify-between gap-4 mb-4">
        <h2 id="et-heading" className="text-base font-semibold text-surface-900">{t('ticketDetail.editDetails')}</h2>
        <button type="button" onClick={onClose} aria-label={t('aria.close')} className="btn-icon -me-2 text-surface-500 hover:text-surface-900 hover:bg-surface-100"><X size={18} aria-hidden="true" /></button>
      </div>
      <form onSubmit={submit} noValidate className="space-y-4">
        {submitted && <FormErrorSummary errors={{ 'et-title': titleError }} labels={{ 'et-title': t('common.title') }} />}
        <Field label={t('common.title')} id="et-title" required error={titleError}>
          <input
            className="input"
            value={title}
            onChange={(e) => { setTitle(e.target.value); if (titleError) setTitleError(checkTitle(e.target.value)); }}
            onBlur={() => setTitleError(checkTitle(title))}
          />
        </Field>
        <Field label={t('common.description')} id="et-desc">
          <textarea className="input min-h-24 text-body-long" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
        </Field>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {canChangeStatus ? (
            <Field
              label={t('common.status')}
              id="et-status"
              hint={statusLocked ? t('ticketDetail.statusLockedCompleted') : undefined}
            >
              <select className="input" value={status} onChange={(e) => setStatus(e.target.value as TicketStatus)} disabled={statusLocked}>
                {statusOptions.map((k) => <option key={k} value={k}>{labelOf(k, ticket.task_type) || getStatusLabel(k as TicketStatus, t, ticket.task_type)}</option>)}
              </select>
            </Field>
          ) : (
            <div>
              <p className="label">{t('common.status')}</p>
              <div className="pt-1.5"><StatusBadge status={status} /></div>
            </div>
          )}
          <Field label={t('common.priority')} id="et-priority">
            <select className="input" value={priority} onChange={(e) => setPriority(e.target.value as TicketPriority)}>
              {Object.entries(getPriorityLabels(t)).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </Field>
          <Field label={t('ticketDetail.assignedAgent')} id="et-agent" className="col-span-2 sm:col-span-1">
            <EntityPicker
              emptyLabel={t('common.unassigned')}
              options={agents.map((a) => ({ value: a.id, label: a.name }))}
              value={agentId}
              onChange={setAgentId}
            />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('tickets.estimatedHours')} id="et-hours">
            <input type="number" inputMode="decimal" step="0.5" min="0" className="input" value={estimatedHours}
              onChange={(e) => setEstimatedHours(e.target.value)} placeholder={t('tickets.noEstimate')} />
          </Field>
          <Field label={t('tickets.dueDate')} id="et-due">
            <DateInput className="input" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </Field>
        </div>
        <Field label={t('tickets.customerSummary')} id="et-summary" hint={t('ticketDetail.customerSummaryHint')}>
          <textarea className="input min-h-16" value={customerSummary}
            onChange={(e) => setCustomerSummary(e.target.value)} rows={2}
            placeholder={t('ticketDetail.customerSummaryPlaceholder')} />
        </Field>
        <ServerError error={mutation.error} fallback={t('ticketDetail.saveFailed')} />
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="btn btn-secondary" disabled={mutation.isPending}>{t('common.cancel')}</button>
          <SubmitButton busy={mutation.isPending}>{t('ticketDetail.save')}</SubmitButton>
        </div>
      </form>
    </section>
  );
}

// ============================================================
// AddWorkLogModal
// ============================================================
export function AddWorkLogModal({ ticketId, ticketTitle, onClose, onSuccess }: {
  ticketId: string;
  ticketTitle?: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const [summary, setSummary] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [billable, setBillable] = useState(true);
  const [billableTouched, setBillableTouched] = useState(false);
  const [consumption, setConsumption] = useState<ConsumptionChoice>({});
  const [consumptionBlocked, setConsumptionBlocked] = useState(false);
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [submitted, setSubmitted] = useState(false);

  const validate = () => ({
    'wl-start': startTime ? undefined : t('ticketDetail.wlStartRequired'),
    'wl-end': !endTime ? t('ticketDetail.wlEndRequired') : startTime && endTime <= startTime ? t('ticketDetail.wlEndAfterStart') : undefined,
    'wl-summary': summary.trim().length < 5 ? t('ticketDetail.wlSummaryMin') : undefined,
  });

  const mutation = useMutation({
    // FU-53: a ticket without an engagement asks on which one to log (like "התחלת עבודה").
    // D-291: `arg` = the shortened times ("לקצר אוטומטית עד").
    mutationFn: (arg: ShortenedTimes | void) => withWorkEngagement({ id: ticketId, title: ticketTitle }, (engagement_id) => api.createWorkLog({
      ticket_id: ticketId,
      start_time: (arg as ShortenedTimes | undefined)?.start_time ?? new Date(startTime).toISOString(),
      end_time: (arg as ShortenedTimes | undefined)?.end_time ?? new Date(endTime).toISOString(),
      summary,
      billable: billableTouched ? billable : undefined,
      hour_bank_id: consumption.hour_bank_id,
      override_reason: consumption.override_reason?.trim() || undefined,
      engagement_id,
      confirm_parallel: (arg as ShortenedTimes | undefined)?.confirm_parallel || undefined, // D-299
    })),
    onSuccess: (res) => { if (!res) return; toastDebtWarning(res); toastWorkLogNotices(res); onSuccess(); }, // D-110, D-266
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const next = validate();
    setErrors(next);
    setSubmitted(true);
    const first = (['wl-start', 'wl-end', 'wl-summary'] as const).find((k) => next[k]);
    if (first) { focusField(first); return; }
    mutation.mutate();
  };
  const blur = (id: 'wl-start' | 'wl-end' | 'wl-summary') => setErrors((prev) => ({ ...prev, [id]: validate()[id] }));

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('ticketDetail.newWorkLogTitle')}
      size="lg"
      dismissible={!mutation.isPending}
      footer={(
        <>
          <button type="button" onClick={onClose} className="btn btn-secondary" disabled={mutation.isPending}>{t('common.cancel')}</button>
          <SubmitButton form="add-wl-form" busy={mutation.isPending} disabled={consumptionBlocked}>{t('ticketDetail.createWorkLogAction')}</SubmitButton>
        </>
      )}
    >
      <form id="add-wl-form" onSubmit={submit} noValidate className="space-y-4">
        {submitted && (
          <FormErrorSummary
            errors={errors}
            labels={{ 'wl-start': t('ticketDetail.startTime'), 'wl-end': t('ticketDetail.endTime'), 'wl-summary': t('ticketDetail.summary') }}
            order={['wl-start', 'wl-end', 'wl-summary']}
          />
        )}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label={t('ticketDetail.startTime')} id="wl-start" required error={errors['wl-start']}>
            <DateTimeInput className="input" value={startTime} onChange={(e) => setStartTime(e.target.value)} onBlur={() => blur('wl-start')} />
          </Field>
          <Field label={t('ticketDetail.endTime')} id="wl-end" required error={errors['wl-end']}>
            <DateTimeInput className="input" value={endTime} onChange={(e) => setEndTime(e.target.value)} onBlur={() => blur('wl-end')} />
          </Field>
        </div>
        <Field label={t('ticketDetail.summary')} id="wl-summary" required error={errors['wl-summary']}>
          <textarea className="input min-h-24 text-body-long" value={summary} onChange={(e) => { setSummary(e.target.value); if (errors['wl-summary']) blur('wl-summary'); }} onBlur={() => blur('wl-summary')} rows={4} />
        </Field>
        <div className="flex items-center gap-2">
          <input type="checkbox" id="add-billable" checked={billable} onChange={(e) => { setBillable(e.target.checked); setBillableTouched(true); }} className="w-4 h-4 accent-primary-700" />
          <label htmlFor="add-billable" className="text-sm text-surface-700">{t('common.billable')}</label>
        </div>
        <ConsumptionPreview
          ticketId={ticketId}
          startTime={startTime}
          endTime={endTime}
          billable={billableTouched ? billable : undefined}
          value={consumption}
          onChange={setConsumption}
          onBlockedChange={setConsumptionBlocked}
          onAutoBillable={(b) => { if (!billableTouched) setBillable(b); }}
        />
        <WorkLogOverlapNotice
          error={mutation.error}
          fallback={t('ticketDetail.createWorkLogFailed')}
          busy={mutation.isPending}
          onShorten={(c) => { const times = shortenedTimes(c); if (times) mutation.mutate(times); }}
          onConfirmParallel={(c) => { const times = parallelConfirmTimes(c); if (times) mutation.mutate(times); }}
          onRetry={() => mutation.mutate(mutation.variables)}
          renderOther={(err) => <ServerError error={err} fallback={t('ticketDetail.createWorkLogFailed')} />}
        />
      </form>
    </Dialog>
  );
}
